import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  Checkbox,
  Chip,
} from '@mui/material';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import {
  ArrowBack,
  Person,
  CalendarToday,
  LocationOn,
  Phone,
  Email,
  Add,
  Delete,
  Edit,
  LocalShipping,
  Description,
  PhotoCamera,
  PictureAsPdf,
  AttachMoney,
  ExpandMore,
  Article,
  ExpandLess,
  Notes,
  Send,
  GetApp,
  Build,
  CheckCircle,
  ZoomIn,
  ZoomOut,
  Print,
  Close,
  Search,
  RotateLeft,
  RotateRight,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parse } from 'date-fns';
import { enGB } from 'date-fns/locale';
import PhotoCapture from '../components/PhotoCapture';
import PhotoGallery from '../components/PhotoGallery';
import DeliveryProgressModal from '../components/DeliveryProgressModal';
import QuotationUpload from '../components/QuotationUpload';
import EmailDocumentModal from '../components/EmailDocumentModal';
import { renderAsync } from 'docx-preview';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Fix for default markers not showing in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface Project {
  id: number;
  title: string;
  nice_name?: string;
  customer_name: string;
  customer_id?: number;
  lead_id?: number;
  status: 'planning' | 'delivery_ready' | 'installation' | 'completed' | 'remedial' | 'remedial_resolved';
  budget: number;
  actual_cost: number;
  deposit_amount?: number;
  start_date: string;
  end_date: string;
  delivery_date?: string;
  customer_delivery_date?: string;
  door_style?: string;
  worktop_style?: string;
  worktop_make?: string;
  worktop_manufacture?: string;
  worktop_thickness?: string;
  worktop_size?: string;
  item_colour?: string;
  back_colour?: string;
  assigned_user: string;
  assigned_to?: number;
  description?: string;
  notes?: string;
  customer_email?: string;
  customer_phone?: string;
  customer_home_phone?: string;
  customer_address?: string;
  customer_address_1?: string;
  customer_address_2?: string;
  customer_address_3?: string;
  customer_town?: string;
  customer_postcode?: string;
  customer_latitude?: number;
  customer_longitude?: number;
  fitter_name?: string;
  fitter_balance?: number;
  fitter_fit_start_date?: string;
  fitter_fit_end_date?: string;
  fitter_details?: string;
  gas_fitter_name?: string;
  gas_fitter_balance?: number;
  gas_fitter_fit_start_date?: string;
  gas_fitter_fit_end_date?: string;
  gas_fitter_details?: string;
  tradesman_trade?: string;
  tradesman_balance?: number;
  tradesman_fit_start_date?: string;
  tradesman_fit_end_date?: string;
  tradesman_details?: string;
  tradesman2_trade?: string;
  tradesman2_balance?: number;
  tradesman2_fit_start_date?: string;
  tradesman2_fit_end_date?: string;
  tradesman2_details?: string;
  tradesman3_trade?: string;
  tradesman3_balance?: number;
  tradesman3_fit_start_date?: string;
  tradesman3_fit_end_date?: string;
  tradesman3_details?: string;
  order_number?: string;
  order_detail_notes?: string;
  order_notes_json?: string;
  material_cost?: number;
  other_costs?: number;
  remedial_description?: string;
  remedial_additional_fitter_cost?: number;
  remedial_additional_delivery_cost?: number;
  remedial_additional_material_cost?: number;
  remedial_status?: 'remedial' | 'resolved';
  remedial_start_date?: string;
  remedial_end_date?: string;
}

interface OrderItem {
  id?: number;
  lead_id?: number;
  project_id?: number;
  supplier_id?: number;
  supplier_name?: string;
  category_id?: number;
  category_name?: string;
  primary_product?: string;
  qty: string;
  hxw: string;
  type: string;
  l: string;
  drillings: string;
  r: string;
  description?: string;
  sq?: string;
  code?: string;
  colour?: string;
  style?: string;
  sort_order?: number;
  supplier_delivery_date?: string;
  note_ref?: number | null;
  isNew?: boolean;
  isDeleting?: boolean;
}

interface Supplier {
  id: number;
  company: string;
}

interface PhotoData {
  id: number;
  filename: string;
  file_path: string;
  fileUrl: string;
  thumbnailUrl: string;
  photo_type: 'before' | 'after' | 'progress' | 'other' | 'document';
  description?: string;
  file_size: number;
  uploaded_by: string;
  uploaded_at: string;
  taken_at?: string;
  latitude?: number;
  longitude?: number;
  mime_type?: string;
  source_table?: string;
  fitter_sent_at?: string;
  customer_sent_at?: string;
  customer_sent_to?: string;
}

interface Payment {
  id?: number;
  project_id?: number;
  amount: number;
  payment_date: string;
  description: string;
  isNew?: boolean;
}

interface Remedial {
  id?: number;
  project_id?: number;
  repair?: string;
  description: string;
  location_of_materials?: string;
  customer_contact_notes?: string;
  date_invoice_paid?: string;
  date_materials_invoice_paid?: string;
  whos_at_fault?: string;
  status: 'open' | 'resolved';
  fitter_cost: number;
  delivery_cost: number;
  material_cost: number;
  reported_date?: string;
  start_date?: string;
  resolved_date?: string;
  delivery_date?: string;
  customer_delivery_date?: string;
  door_style?: string;
  worktop_style?: string;
  worktop_make?: string;
  worktop_manufacture?: string;
  worktop_thickness?: string;
  worktop_size?: string;
  item_colour?: string;
  back_colour?: string;
  created_at?: string;
  updated_at?: string;
  isNew?: boolean;
}

export default function ProjectDetail() {
  // Email functionality added for pictures and documents
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [originalProject, setOriginalProject] = useState<Project | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [remedialError, setRemedialError] = useState<string | null>(null);
  const [dirtyPasteFields, setDirtyPasteFields] = useState<string[]>([]);
  const [fieldIssues, setFieldIssues] = useState<{[label: string]: string}>({});
  const [loading, setLoading] = useState(true);
  const [photos, setPhotos] = useState<PhotoData[]>([]);
  const [photoRotations, setPhotoRotations] = useState<{[key: number]: number}>({});
  const [documentRotations, setDocumentRotations] = useState<{[key: number]: number}>({});
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [saving, setSaving] = useState(false);
  const [autoSaveTimeout, setAutoSaveTimeout] = useState<NodeJS.Timeout | null>(null);
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
  const [deliveryProgress, setDeliveryProgress] = useState(0);
  const [deliveryError, setDeliveryError] = useState<string | null>(null);
  const [deliveryPdfUrl, setDeliveryPdfUrl] = useState<string | undefined>();
  const [deliveryNumber, setDeliveryNumber] = useState<string | undefined>();
  const [isGeneratingDelivery, setIsGeneratingDelivery] = useState(false);
  const [documents, setDocuments] = useState<any[]>([]);
  const [creatingLetter, setCreatingLetter] = useState(false);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [newPayment, setNewPayment] = useState<Payment>({ amount: 0, payment_date: format(new Date(), 'yyyy-MM-dd'), description: '' });
  const [remedials, setRemedials] = useState<Remedial[]>([]);
  const [remedialOrderItems, setRemedialOrderItems] = useState<Map<number, OrderItem[]>>(new Map());
  const [newRemedial, setNewRemedial] = useState<Remedial>({
    repair: '',
    description: '',
    location_of_materials: '',
    customer_contact_notes: '',
    date_invoice_paid: '',
    date_materials_invoice_paid: '',
    whos_at_fault: '',
    status: 'open',
    fitter_cost: 0,
    delivery_cost: 0,
    material_cost: 0,
    reported_date: format(new Date(), 'yyyy-MM-dd'),
  });
  const [showAddRemedialForm, setShowAddRemedialForm] = useState(false);
  const [deleteRemedialId, setDeleteRemedialId] = useState<number | null>(null);
  const [showPicturesUpload, setShowPicturesUpload] = useState(false);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [uploadingPictures, setUploadingPictures] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [sendingToSupplier, setSendingToSupplier] = useState<number | null>(null);
  const [supplierSendSuccess, setSupplierSendSuccess] = useState<{docId: number, message: string} | null>(null);
  const [supplierSendModalOpen, setSupplierSendModalOpen] = useState(false);
  const [supplierSendDoc, setSupplierSendDoc] = useState<any>(null);
  const [supplierSendDocs, setSupplierSendDocs] = useState<any[]>([]);
  const [supplierSendInfo, setSupplierSendInfo] = useState<{supplierEmail: string, supplierName: string, ccEmails: string[], fileName: string, fileNames: string[]} | null>(null);
  const [loadingSupplierInfo, setLoadingSupplierInfo] = useState(false);
  const [supplierSentModalOpen, setSupplierSentModalOpen] = useState(false);
  const [supplierSentDetails, setSupplierSentDetails] = useState<{sentTo: string, ccTo: string[], fileName: string} | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [docxPreviewLoading, setDocxPreviewLoading] = useState(false);
  const docxContainerRef = React.useRef<HTMLDivElement>(null);
  const [mapCoordinates, setMapCoordinates] = useState<[number, number] | null>(null);
  const [postcodeDialogOpen, setPostcodeDialogOpen] = useState(false);
  const [postcodeSearch, setPostcodeSearch] = useState('');
  const [addressOptions, setAddressOptions] = useState<Array<{postcode: string, postTown: string, address1: string, county: string}>>([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [orderItemsExpanded, setOrderItemsExpanded] = useState(true);
  const [orderDetailsExpanded, setOrderDetailsExpanded] = useState(false);
  const [orderChecklistExpanded, setOrderChecklistExpanded] = useState(false);
  const [orderChecklist, setOrderChecklist] = useState<any>(null);
  const [issueOrderModalOpen, setIssueOrderModalOpen] = useState(false);
  const [supplierOrderOptions, setSupplierOrderOptions] = useState<any[]>([]);
  const [selectedSupplierKeys, setSelectedSupplierKeys] = useState<string[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(true);
  const [leadNotes, setLeadNotes] = useState<string>('');
  const [originalLeadNotes, setOriginalLeadNotes] = useState<string>('');
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedDocForEmail, setSelectedDocForEmail] = useState<any>(null);
  const [selectedDocsForEmail, setSelectedDocsForEmail] = useState<any[]>([]);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<Set<number>>(new Set());
  const [selectedDocIds, setSelectedDocIds] = useState<Set<number>>(new Set());
  const [renamePhotoDialog, setRenamePhotoDialog] = useState<{ open: boolean; photo: any | null; newName: string }>({
    open: false,
    photo: null,
    newName: ''
  });
  const [users, setUsers] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [activeFitters, setActiveFitters] = useState<{id: number; name: string; specialty: string}[]>([]);

  // Tablet detection for performance optimizations (touch device with decent screen size)
  const isTablet = 'ontouchstart' in window && window.innerWidth >= 768;
  // Mobile/tablet detection for docx preview (touch devices or small screens)
  const isMobileOrTablet = 'ontouchstart' in window || window.innerWidth < 1024;

  // Lazy loading state for tablets
  const [mapLoaded, setMapLoaded] = useState(!isTablet); // Load immediately on desktop
  const [photosLoaded, setPhotosLoaded] = useState(!isTablet);
  const [documentsLoaded, setDocumentsLoaded] = useState(!isTablet);
  const [paymentsLoaded, setPaymentsLoaded] = useState(!isTablet);
  const [remedialsLoaded, setRemedialsLoaded] = useState(!isTablet);

  // Log unhandled errors to console only — do NOT call setError here as calling setState
  // from window.onerror fires during React's render cycle and causes error #185 (too many re-renders)
  React.useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      console.error('Global error in ProjectDetail:', event.error);
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  // Load docx preview for mobile/tablet
  const [docxError, setDocxError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!selectedDocument?.isWordDoc || !isMobileOrTablet) {
      return;
    }

    setDocxError(null);

    // Small delay to ensure the DOM container is mounted
    const timeoutId = setTimeout(async () => {
      if (!docxContainerRef.current) {
        setDocxError('Container not ready - please try again');
        return;
      }

      setDocxPreviewLoading(true);
      try {
        const url = selectedDocument.viewUrl || `/api/documents/download/${selectedDocument.id}`;
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
        }

        const blob = await response.blob();

        if (blob.size === 0) {
          throw new Error('Empty file received');
        }

        // Clear previous content
        docxContainerRef.current.innerHTML = '';

        // Render the docx
        await renderAsync(blob, docxContainerRef.current, undefined, {
          className: 'docx',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          ignoreLastRenderedPageBreak: true,
          experimental: false,
          trimXmlDeclaration: true,
          useBase64URL: true,
        });
      } catch (error: any) {
        console.error('Error loading docx preview:', error);
        setDocxError(error?.message || 'Unable to load preview');
        if (docxContainerRef.current) {
          docxContainerRef.current.innerHTML = '';
        }
      } finally {
        setDocxPreviewLoading(false);
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [selectedDocument, isMobileOrTablet]);

  // Fetch documents for the project
  const fetchDocuments = async () => {
    if (!id) return;

    try {
      console.log('Fetching documents for project:', id);
      const response = await axios.get(`${API_BASE_URL}/projects/${id}/documents`);
      console.log('Documents response:', response.data);
      // Filter out invoice documents
      const filteredDocs = (response.data.documents || []).filter(
        (doc: any) => doc.related_to !== 'Invoice'
      );
      setDocuments(filteredDocs);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  // Fetch invoices for the project
  const fetchInvoices = async () => {
    if (!id) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/invoices`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      // Filter invoices for this project
      const projectInvoices = (response.data.invoices || []).filter(
        (inv: any) => inv.project_id === parseInt(id)
      );
      setInvoices(projectInvoices);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  // Fetch photos for the project
  const fetchPhotos = async () => {
    if (!id) return;

    try {
      const response = await axios.get(`${API_BASE_URL}/project-photos/${id}/photos`);
      const photosData = response.data.photos || [];
      setPhotos(photosData);

      // Initialize rotations from loaded photos
      const rotations: {[key: number]: number} = {};
      photosData.forEach((photo: any) => {
        if (photo.rotation) {
          rotations[photo.id] = photo.rotation;
        }
      });
      setPhotoRotations(rotations);
    } catch (error) {
      console.error('Error fetching photos:', error);
    }
  };

  // Delete photo
  const handleDeletePhoto = async (photoId: number, sourceTable?: string) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      // Use different endpoint based on source table
      const endpoint = sourceTable === 'documents'
        ? `/api/documents/${photoId}`
        : `/api/project-photos/photo/${photoId}`;

      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        // Remove photo from state
        setPhotos(prevPhotos => prevPhotos.filter(photo => photo.id !== photoId));
        console.log('Photo deleted successfully');
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to delete photo');
      }
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Failed to delete photo. Please try again.');
    }
  };

  // Rename photo
  const handleRenamePhoto = async () => {
    if (!renamePhotoDialog.photo || !renamePhotoDialog.newName.trim()) {
      return;
    }

    try {
      const response = await fetch(`/api/project-photos/${id}/photos/${renamePhotoDialog.photo.id}/rename`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          description: renamePhotoDialog.newName.trim(),
          source_table: renamePhotoDialog.photo.source_table
        }),
      });

      if (response.ok) {
        // Update photo in state
        setPhotos(prevPhotos => prevPhotos.map(photo =>
          photo.id === renamePhotoDialog.photo.id
            ? { ...photo, description: renamePhotoDialog.newName.trim() }
            : photo
        ));
        setRenamePhotoDialog({ open: false, photo: null, newName: '' });
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to rename photo');
      }
    } catch (error) {
      console.error('Error renaming photo:', error);
      alert('Failed to rename photo. Please try again.');
    }
  };

  // Rotate photo and save to database
  const handleRotatePhoto = async (photoId: number, direction: 'left' | 'right', sourceTable?: string) => {
    // Only save rotation for project_photos, not documents from leads
    const canSave = sourceTable !== 'documents';

    const currentRotation = photoRotations[photoId] || 0;
    const newRotation = direction === 'right'
      ? (currentRotation + 90) % 360
      : (currentRotation - 90 + 360) % 360;

    // Update UI immediately
    setPhotoRotations(prev => ({ ...prev, [photoId]: newRotation }));

    // Save to database if it's a project photo
    if (canSave) {
      try {
        await axios.put(`${API_BASE_URL}/project-photos/photo/${photoId}/rotate`, {
          rotation: newRotation
        });
      } catch (error) {
        console.error('Error saving photo rotation:', error);
      }
    }
  };

  // Rotate document (client-side only for now)
  const handleRotateDocument = (docId: number, direction: 'left' | 'right') => {
    const currentRotation = documentRotations[docId] || 0;
    const newRotation = direction === 'right'
      ? (currentRotation + 90) % 360
      : (currentRotation - 90 + 360) % 360;
    setDocumentRotations(prev => ({ ...prev, [docId]: newRotation }));
  };

  // Fetch order items
  const fetchOrderItems = async () => {
    if (!id) return;

    try {
      const response = await axios.get(`${API_BASE_URL}/projects/${id}/order-items`);
      setOrderItems(response.data || []);
    } catch (error) {
      console.error('Error fetching order items:', error);
    }
  };

  // Fetch order items for all remedials
  const fetchRemedialOrderItems = async (remedialsList: Remedial[]) => {
    const newRemedialOrderItems = new Map<number, OrderItem[]>();

    for (const remedial of remedialsList) {
      if (remedial.id) {
        try {
          const response = await axios.get(`${API_BASE_URL}/remedials/${remedial.id}/order-items`);
          newRemedialOrderItems.set(remedial.id, response.data || []);
        } catch (error) {
          console.error(`Error fetching order items for remedial ${remedial.id}:`, error);
          newRemedialOrderItems.set(remedial.id, []);
        }
      }
    }

    setRemedialOrderItems(newRemedialOrderItems);
  };

  // Fetch suppliers
  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/suppliers`);
      setSuppliers(response.data || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  // Fetch order checklist
  const fetchOrderChecklist = async () => {
    if (!id) return;

    try {
      const response = await axios.get(`${API_BASE_URL}/orderformchecklist/project/${id}`);
      setOrderChecklist(response.data.checklist || {});
    } catch (error) {
      console.error('Error fetching order checklist:', error);
      // Initialize empty checklist if none exists
      setOrderChecklist({});
    }
  };

  // Fetch users for dropdowns
  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/non-admin`);
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Fetch payments
  const fetchPayments = async () => {
    if (!id) return;
    
    try {
      const response = await fetch(`/api/projects/${id}/payments`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setPayments(data.payments || []);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  };

  // Fetch lead notes
  const fetchLeadNotes = async (leadId: number) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/leads/${leadId}`);
      
      let notes = response.data.lead?.notes;
      if (notes === null || notes === undefined) {
        notes = '';
      }
      setLeadNotes(notes);
      setOriginalLeadNotes(notes);
    } catch (error) {
      console.error('Error fetching lead notes:', error);
      // Set to empty string on error to maintain controlled component
      setLeadNotes('');
      setOriginalLeadNotes('');
    }
  };

  // Update lead notes
  const updateLeadNotes = async (notes: string) => {
    if (!project?.lead_id) return;
    
    // Ensure notes is always a string
    const safeNotes = (notes === null || notes === undefined) ? '' : notes;
    const safeOriginalNotes = (originalLeadNotes === null || originalLeadNotes === undefined) ? '' : originalLeadNotes;
    
    // Only update if notes have changed
    if (safeNotes === safeOriginalNotes) return;
    
    try {
      const response = await fetch(`/api/leads/${project?.lead_id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notes: safeNotes }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setLeadNotes(safeNotes);
        setOriginalLeadNotes(safeNotes); // Update original after successful save
        console.log('Lead notes updated successfully');
      } else {
        console.error('Failed to update lead notes');
        // Revert to original value on error
        setLeadNotes(safeOriginalNotes);
      }
    } catch (error) {
      console.error('Error updating lead notes:', error);
      // Revert to original value on error
      setLeadNotes(safeOriginalNotes);
    }
  };

  // Geocode address to get coordinates using postcodes.io
  const geocodeAddress = async (address: string) => {
    if (!address) return;
    
    try {
      // Extract postcode from address (UK postcode pattern)
      const postcodeMatch = address.match(/([A-Z]{1,2}\d{1,2}[A-Z]?\s*\d[A-Z]{2})/i);
      
      if (postcodeMatch) {
        const postcode = postcodeMatch[1].replace(/\s+/g, '');
        
        // Use postcodes.io for UK postcode geocoding
        const response = await fetch(
          `https://api.postcodes.io/postcodes/${postcode}`
        );
        const data = await response.json();
        
        if (data.status === 200 && data.result) {
          const lat = data.result.latitude;
          const lon = data.result.longitude;
          setMapCoordinates([lat, lon]);
        }
      }
    } catch (error) {
      console.error('Error geocoding address:', error);
      
      // Fallback: Try server-side geocoding
      try {
        const postcodeMatch = address.match(/([A-Z]{1,2}\d{1,2}[A-Z]?\s*\d[A-Z]{2})/i);
        if (postcodeMatch) {
          const postcode = postcodeMatch[1].replace(/\s+/g, '');
          const token = localStorage.getItem('token');
          const response = await fetch(`/api/postcode/coordinates/${postcode}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              setMapCoordinates([data.latitude, data.longitude]);
            }
          }
        }
      } catch (fallbackError) {
        console.error('Fallback geocoding also failed:', fallbackError);
      }
    }
  };

  // Postcode lookup functions
  const handleOpenPostcodeLookup = () => {
    setPostcodeSearch(project?.customer_postcode || '');
    setAddressOptions([]);
    setPostcodeDialogOpen(true);
  };

  const handlePostcodeLookup = async () => {
    if (!postcodeSearch.trim()) {
      alert('Please enter a postcode');
      return;
    }

    setLoadingAddresses(true);
    try {
      const cleanPostcode = postcodeSearch.replace(/\s+/g, '').toUpperCase();
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/postcode/lookup/${cleanPostcode}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = response.data;
      if (data.success && data.addresses.length > 0) {
        setAddressOptions(data.addresses);
      } else if (data.success && data.addresses.length === 0) {
        alert('No addresses found for this postcode');
        setAddressOptions([]);
      } else {
        alert('Error looking up postcode');
        setAddressOptions([]);
      }
    } catch (error) {
      console.error('Error looking up postcode:', error);
      alert('Error looking up postcode');
    } finally {
      setLoadingAddresses(false);
    }
  };

  const handleSelectAddress = async (address: { postcode: string; postTown: string; address1: string; county: string }) => {
    // Get coordinates from postcodes.io
    let latitude = null;
    let longitude = null;

    try {
      const cleanPostcode = address.postcode.replace(/\s+/g, '');
      const geocodeUrl = `https://api.postcodes.io/postcodes/${cleanPostcode}`;

      const response = await fetch(geocodeUrl);
      const data = await response.json();

      if (data.status === 200 && data.result) {
        latitude = data.result.latitude;
        longitude = data.result.longitude;
        setMapCoordinates([latitude, longitude]);
      }
    } catch (error) {
      console.error('Error getting postcode coordinates:', error);
    }

    // Update the project state
    const updatedProject = {
      ...project!,
      customer_address_1: address.address1,
      customer_address_2: '',
      customer_address_3: address.county || '',
      customer_town: address.postTown,
      customer_postcode: address.postcode,
      customer_latitude: latitude,
      customer_longitude: longitude
    };
    setProject(updatedProject);

    // Save to database
    await saveCustomerAddress(updatedProject);

    setPostcodeDialogOpen(false);
    setAddressOptions([]);
  };

  const saveCustomerAddress = async (projectData: Project) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/projects/${id}/customer-address`, {
        customer_address_1: projectData.customer_address_1,
        customer_address_2: projectData.customer_address_2,
        customer_address_3: projectData.customer_address_3,
        customer_town: projectData.customer_town,
        customer_postcode: projectData.customer_postcode,
        customer_latitude: projectData.customer_latitude,
        customer_longitude: projectData.customer_longitude
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('Customer address saved successfully');
    } catch (error) {
      console.error('Error saving customer address:', error);
    }
  };

  const handleAddressFieldBlur = async () => {
    if (project) {
      await saveCustomerAddress(project);
      // Geocode the new postcode if it changed
      if (project.customer_postcode) {
        try {
          const cleanPostcode = project.customer_postcode.replace(/\s+/g, '');
          const response = await fetch(`https://api.postcodes.io/postcodes/${cleanPostcode}`);
          const data = await response.json();
          if (data.status === 200 && data.result) {
            setMapCoordinates([data.result.latitude, data.result.longitude]);
          }
        } catch (error) {
          console.error('Error geocoding postcode:', error);
        }
      }
    }
  };

  // Handle delivery note generation
  const handleCreateDelivery = async () => {
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        alert('Please log in again to create a delivery note');
        window.location.href = '/login';
        return;
      }

      const response = await fetch(`/api/projects/${id}/build-delivery`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to generate delivery note' }));
        throw new Error(errorData.message || 'Failed to generate delivery note');
      }

      const data = await response.json();

      // Show success message
      alert(`Delivery note created successfully! ${data.delivery_number || ''}`);

      // Refresh documents list to show new delivery note
      await fetchDocuments();

      // Refresh project details to show updated status
      await fetchProject();

    } catch (error) {
      console.error('Error generating delivery note:', error);
      alert(error instanceof Error ? error.message : 'Failed to generate delivery note');
    }
  };

  // Handle letter generation
  const handleCreateLetter = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Check if token exists
      if (!token) {
        alert('Please log in again to create a letter');
        window.location.href = '/login';
        return;
      }
      
      const response = await fetch(`/api/letter-generator/projects/${id}/create-letter`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create letter');
      }

      const data = await response.json();
      
      // Show success message
      alert(`Letter created successfully! ${data.filename}`);
      
      // Refresh documents list to show new letter
      await fetchDocuments();
      
    } catch (error) {
      console.error('Error creating letter:', error);
      alert(error instanceof Error ? error.message : 'Failed to create letter');
    }
  };

  // Fetch project details function
  const fetchProject = async (silent = false) => {
    if (!id) {
      if (!silent) setError('Project ID is missing');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/projects/${id}`);

      setProject(response.data.project);
      setOriginalProject(response.data.project); // Store original for comparison
      // Fetch lead notes if lead_id exists
      if (response.data.project?.lead_id) {
        fetchLeadNotes(response.data.project.lead_id);
      }
    } catch (error: any) {
      console.error('Error fetching project:', error);
      if (!silent) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch project data';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch project details on mount
  useEffect(() => {
    // Always load core data
    fetchProject();
    fetchOrderItems();
    fetchSuppliers();
    fetchOrderChecklist();
    fetchUsers();
    fetchRemedials(); // Always load remedials on all devices

    // Fetch active fitters for dropdowns
    axios.get(`${API_BASE_URL}/fitters/active`).then(res => setActiveFitters(res.data)).catch(() => {});

    // On tablets, defer loading of secondary data until sections are expanded
    if (!isTablet) {
      fetchPhotos();
      fetchDocuments();
      fetchInvoices();
      fetchPayments();
    }
  }, [id]);

  // Set map coordinates when project data is loaded
  useEffect(() => {
    if (project?.customer_latitude && project?.customer_longitude) {
      // Use stored coordinates if available
      setMapCoordinates([project.customer_latitude, project.customer_longitude]);
    } else if (project?.customer_postcode) {
      // Otherwise geocode from postcode
      const fetchCoords = async () => {
        try {
          const cleanPostcode = project.customer_postcode!.replace(/\s+/g, '');
          const response = await fetch(`https://api.postcodes.io/postcodes/${cleanPostcode}`);
          const data = await response.json();
          if (data.status === 200 && data.result) {
            setMapCoordinates([data.result.latitude, data.result.longitude]);
          }
        } catch (error) {
          console.error('Error geocoding postcode:', error);
        }
      };
      fetchCoords();
    }
  }, [project?.customer_latitude, project?.customer_longitude, project?.customer_postcode]);

  // Auto-save function for inline updates
  const autoSaveField = async (field: string, value: any) => {
    if (!project || !id) return;

    try {
      const response = await fetch(`/api/projects/${id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ [field]: value }),
      });

      if (response.ok) {
        const data = await response.json();
        setProject(data.project);
        setOriginalProject(data.project); // Update original after successful save
      } else {
        console.error('Failed to update field:', field);
        // Revert to original value on error
        if (originalProject) {
          setProject(originalProject);
        }
      }
    } catch (error) {
      console.error('Error updating field:', error);
    }
  };

  // Auto-save function for checklist inline updates
  const autoSaveChecklistField = async (field: string, value: any) => {
    if (!id) return;

    try {
      const response = await fetch(`/api/orderformchecklist/project/${id}/field`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ field, value }),
      });

      if (response.ok) {
        // Update local state
        setOrderChecklist((prev: any) => ({
          ...prev,
          [field]: value
        }));
      } else {
        console.error('Failed to update checklist field:', field);
      }
    } catch (error) {
      console.error('Error updating checklist field:', error);
    }
  };

  // Handle field blur for auto-save
  const handleFieldBlur = (field: string, value: any) => {
    if (originalProject && originalProject[field as keyof Project] !== value) {
      autoSaveField(field, value);
    }
  };

  // Format date for display (DD/MM/YYYY)
  const formatDateForDisplay = (dateString: string | null | undefined) => {
    if (!dateString || dateString === 'null') return '';
    try {
      const date = new Date(dateString);
      return format(date, 'dd/MM/yyyy');
    } catch {
      return '';
    }
  };

  // Parse date from display format for saving
  const parseDateFromDisplay = (dateString: string) => {
    if (!dateString) return null;
    try {
      return format(parse(dateString, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd');
    } catch {
      return null;
    }
  };

  // Safe date parsing function
  const parseProjectDate = (dateValue: string | null | undefined) => {
    if (!dateValue || dateValue === 'null' || dateValue === 'undefined' || dateValue === '') {
      return null;
    }
    try {
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  };

  // Handle date change
  const handleDateChange = (field: string, date: Date | null) => {
    if (!project) return;
    
    if (date && !isNaN(date.getTime())) {
      const formattedDate = format(date, 'yyyy-MM-dd');
      setProject({ ...project, [field]: formattedDate });
      autoSaveField(field, formattedDate);
    } else {
      setProject({ ...project, [field]: null });
      autoSaveField(field, null);
    }
  };

  // Handle picture upload
  const handlePicturesUpload = async (files: File[]) => {
    if (!id || files.length === 0) return;

    setUploadingPictures(true);
    try {
      const token = localStorage.getItem('token');
      
      // Create FormData and append all files at once
      const formData = new FormData();
      
      // Append each file with the correct field name 'photos'
      for (const file of files) {
        formData.append('photos', file);
      }
      
      // Add optional photo metadata
      formData.append('photoType', 'other');

      // Use the correct project-photos endpoint
      const response = await fetch(`/api/project-photos/${id}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.text();
        console.error('Upload failed:', error);
        throw new Error('Failed to upload pictures');
      }

      const result = await response.json();
      console.log('Upload successful:', result);

      // Refresh photos list to show new uploads
      await fetchPhotos();
      setShowPicturesUpload(false);
    } catch (error) {
      console.error('Error uploading pictures:', error);
      alert('Failed to upload pictures. Please try again.');
    } finally {
      setUploadingPictures(false);
    }
  };

  // Handle document upload
  const handleDocumentUpload = async (files: File[]) => {
    if (!id || files.length === 0) return;

    setUploadingDocument(true);
    try {
      const token = localStorage.getItem('token');
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('related_to', 'project');
        formData.append('related_id', id);
        formData.append('document_name', `Document - ${file.name}`);

        const response = await fetch('/api/documents/upload', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        });

        if (!response.ok) {
          throw new Error('Failed to upload document');
        }
      }

      // Refresh documents
      await fetchDocuments();
      setShowDocumentUpload(false);
    } catch (error) {
      console.error('Error uploading document:', error);
    } finally {
      setUploadingDocument(false);
    }
  };

  // Handle delete document
  const handleDeleteDocument = async (docId: number, sourceType?: string) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      // Use different endpoint based on source type
      let endpoint;
      if (sourceType === 'lead' || sourceType === 'remedial') {
        endpoint = `/api/documents/${docId}`;
      } else {
        endpoint = `/api/projects/${id}/documents/${docId}`;
      }

      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        await fetchDocuments();
      } else {
        const error = await response.json();
        alert(`Failed to delete document: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    }
  };

  // Delete multiple selected documents
  const handleDeleteSelectedDocuments = async () => {
    if (selectedDocIds.size === 0) {
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedDocIds.size} selected document(s)?`)) {
      return;
    }

    try {
      const deletePromises = Array.from(selectedDocIds).map(async (docId) => {
        // Find the document to get its source type
        const doc = documents.find((d: any) => d.id === docId);
        const sourceType = doc?.source_type;

        // Use different endpoint based on source type
        let endpoint;
        if (sourceType === 'lead' || sourceType === 'remedial') {
          endpoint = `/api/documents/${docId}`;
        } else {
          endpoint = `/api/projects/${id}/documents/${docId}`;
        }

        const response = await fetch(endpoint, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(`Failed to delete document ${docId}: ${error.message}`);
        }
      });

      await Promise.all(deletePromises);

      // Clear selection and refresh documents
      setSelectedDocIds(new Set());
      await fetchDocuments();

      alert(`Successfully deleted ${selectedDocIds.size} document(s)`);
    } catch (error) {
      console.error('Error deleting documents:', error);
      alert(`Failed to delete some documents: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Open supplier send confirmation modal (supports single doc or array of docs)
  const handleOpenSupplierSendModal = async (docOrDocs: any) => {
    const docs = Array.isArray(docOrDocs) ? docOrDocs : [docOrDocs];
    setSupplierSendDoc(docs[0]);
    setSupplierSendDocs(docs);
    setSupplierSendModalOpen(true);
    setLoadingSupplierInfo(true);
    setSupplierSendInfo(null);

    try {
      const token = localStorage.getItem('token');

      // Get supplier info from first document name
      const docName = docs[0].original_name || docs[0].description || docs[0].document_name || docs[0].filename || '';
      const supplierMatch = docName.match(/Supplier Order - (.+)\.docx/);
      // Strip category suffix like "(Accessories)" from supplier name for database lookup
      const rawSupplierName = supplierMatch ? supplierMatch[1] : 'Unknown';
      const supplierName = rawSupplierName.replace(/\s*\([^)]+\)\s*$/, '').trim();

      // Fetch supplier details
      const supplierResponse = await fetch(`/api/suppliers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const suppliers = await supplierResponse.json();
      const supplier = suppliers.find((s: any) => s.company === supplierName);

      // Fetch CC emails from settings
      const settingsResponse = await fetch(`/api/settings/supplier_cc_emails`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const settingsData = await settingsResponse.json();
      let ccEmails: string[] = [];
      if (settingsData.value) {
        try {
          ccEmails = JSON.parse(settingsData.value);
        } catch (e) {
          ccEmails = [];
        }
      }

      const fileNames = docs.map((d: any) => d.description || d.original_name || d.document_name || d.filename);

      setSupplierSendInfo({
        supplierEmail: supplier?.email || 'No email found',
        supplierName: supplierName,
        ccEmails: ccEmails,
        fileName: fileNames[0],
        fileNames: fileNames
      });
    } catch (error) {
      console.error('Error fetching supplier info:', error);
      const fileNames = docs.map((d: any) => d.description || d.original_name || d.document_name || d.filename);
      setSupplierSendInfo({
        supplierEmail: 'Error loading',
        supplierName: 'Unknown',
        ccEmails: [],
        fileName: fileNames[0],
        fileNames: fileNames
      });
    } finally {
      setLoadingSupplierInfo(false);
    }
  };

  // Send supplier order(s) to supplier via email
  const handleConfirmSendToSupplier = async () => {
    if (!supplierSendDoc) return;

    const docs = supplierSendDocs.length > 0 ? supplierSendDocs : [supplierSendDoc];
    setSupplierSendModalOpen(false);

    try {
      setSendingToSupplier(docs[0].id);
      setSupplierSendSuccess(null);
      const token = localStorage.getItem('token');

      let response;
      if (docs.length > 1) {
        // Multiple documents - use bulk endpoint
        response = await fetch('/api/email/send-supplier-orders', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            documentIds: docs.map((d: any) => d.id),
            projectId: id
          })
        });
      } else {
        // Single document - use existing endpoint
        response = await fetch('/api/email/send-supplier-order', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            documentId: docs[0].id,
            projectId: id
          })
        });
      }

      const result = await response.json();

      if (response.ok && result.success) {
        setSupplierSendSuccess({
          docId: docs[0].id,
          message: result.message
        });
        // Show success modal
        const fileNames = supplierSendInfo?.fileNames || docs.map((d: any) => d.original_name || d.document_name);
        setSupplierSentDetails({
          sentTo: result.sentTo,
          ccTo: result.ccTo || [],
          fileName: fileNames.join(', ')
        });
        setSupplierSentModalOpen(true);
        // Refresh documents to get updated supplier_sent_at
        fetchDocuments();
        // Clear selection
        setSelectedDocIds(new Set());
      } else {
        setSnackbarMessage(result.message || 'Unable to send email right now. Your order has been saved and you can try again later.');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error sending to supplier:', error);
      setSnackbarMessage('Unable to send email right now. Your order has been saved and you can try again later.');
      setSnackbarOpen(true);
    } finally {
      setSendingToSupplier(null);
      setSupplierSendDoc(null);
      setSupplierSendDocs([]);
    }
  };

  // Add payment
  const handleAddPayment = async () => {
    if (!id || !newPayment.amount || !newPayment.description) return;

    try {
      const response = await fetch(`/api/projects/${id}/payments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newPayment),
      });

      if (response.ok) {
        await fetchPayments();
        setNewPayment({ amount: 0, payment_date: format(new Date(), 'yyyy-MM-dd'), description: '' });
      }
    } catch (error) {
      console.error('Error adding payment:', error);
    }
  };

  // Delete payment
  const handleDeletePayment = async (paymentId: number) => {
    try {
      const response = await fetch(`/api/projects/${id}/payments/${paymentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        await fetchPayments();
      }
    } catch (error) {
      console.error('Error deleting payment:', error);
    }
  };

  // ==================== REMEDIAL FUNCTIONS ====================

  // Fetch remedials for project
  const fetchRemedials = async () => {
    if (!id) return;

    try {
      const response = await fetch(`/api/projects/${id}/remedials`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Sort by reported_date (oldest first), falling back to created_at
        const sorted = (data.remedials || []).sort((a: Remedial, b: Remedial) => {
          const dateA = a.reported_date || a.created_at;
          const dateB = b.reported_date || b.created_at;
          return new Date(dateA || 0).getTime() - new Date(dateB || 0).getTime();
        });
        setRemedials(sorted);
        // Fetch order items for each remedial
        await fetchRemedialOrderItems(sorted);
      }
    } catch (error) {
      console.error('Error fetching remedials:', error);
    }
  };

  // Check a field value for problematic characters and update fieldIssues state
  const checkFieldForIssues = (fieldLabel: string, value: string) => {
    const found: string[] = [];
    if (/\u0000/.test(value)) found.push('null character');
    if (/[\u0001-\u0008\u000B\u000C\u000E-\u001F\u007F]/.test(value)) found.push('invisible control characters');
    if (/[\u200B\u200C\u200D\uFEFF]/.test(value)) found.push('hidden zero-width characters');
    if (/[\u00AD]/.test(value)) found.push('soft hyphen');

    setFieldIssues(prev => {
      if (found.length > 0) return { ...prev, [fieldLabel]: found.join(', ') };
      const next = { ...prev };
      delete next[fieldLabel];
      return next;
    });
  };

  // Detect and strip formatted/dirty paste in remedial text fields
  const handleRemedialPaste = (fieldName: keyof typeof newRemedial, fieldLabel: string) =>
    (e: React.ClipboardEvent) => {
      const html = e.clipboardData.getData('text/html');
      // Only flag if clipboard has HTML content with actual tags (i.e. from Word, email, website)
      if (html && /<[a-z]/i.test(html)) {
        e.preventDefault();
        const plain = e.clipboardData.getData('text/plain');
        setNewRemedial(prev => ({ ...prev, [fieldName]: (prev[fieldName] || '') + plain }));
        setDirtyPasteFields(prev => prev.includes(fieldLabel) ? prev : [...prev, fieldLabel]);
      }
    };

  // Add new remedial issue
  const handleAddRemedial = async () => {
    if (!id) return;
    setRemedialError(null);

    // Sanitize cost fields — strip currency symbols/commas that cause database errors if pasted
    const parseCost = (val: any) => parseFloat(String(val).replace(/[^0-9.]/g, '')) || 0;
    const sanitizedRemedial = {
      ...newRemedial,
      fitter_cost: parseCost(newRemedial.fitter_cost),
      delivery_cost: parseCost(newRemedial.delivery_cost),
      material_cost: parseCost(newRemedial.material_cost),
    };

    try {
      const response = await fetch(`/api/projects/${id}/remedials`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sanitizedRemedial),
      });

      if (response.ok) {
        await fetchRemedials();
        await fetchProject(true); // Refresh project status — silent so failure doesn't replace page
        setNewRemedial({
          repair: '',
          description: '',
          location_of_materials: '',
          customer_contact_notes: '',
          date_invoice_paid: '',
          date_materials_invoice_paid: '',
          whos_at_fault: '',
          status: 'open',
          fitter_cost: 0,
          delivery_cost: 0,
          material_cost: 0,
          reported_date: format(new Date(), 'yyyy-MM-dd'),
        });
        setDirtyPasteFields([]);
        setFieldIssues({});
        setShowAddRemedialForm(false); // Close the form after successful add
      } else {
        const errorData = await response.json().catch(() => ({}));
        const serverMsg = errorData.message || 'Unknown error';
        setRemedialError(
          `Could not save the remedial issue (${serverMsg}). ` +
          'If you copied and pasted text into any field, please try typing it in manually — ' +
          'pasted text can sometimes contain hidden characters that cannot be stored.'
        );
      }
    } catch (error) {
      console.error('Error adding remedial:', error);
      setRemedialError('A network error occurred. Please check your connection and try again.');
    }
  };

  // Update remedial issue (mainly for status changes)
  const handleUpdateRemedial = async (remedialId: number, updates: Partial<Remedial>) => {
    if (!id) return;

    try {
      const response = await fetch(`/api/projects/${id}/remedials/${remedialId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      if (response.ok) {
        await fetchRemedials();
        await fetchProject(true); // Refresh project status — silent so failure doesn't replace page
      }
    } catch (error) {
      console.error('Error updating remedial:', error);
    }
  };

  // Delete remedial issue
  const handleDeleteRemedial = async (remedialId: number) => {
    if (!id) return;

    try {
      const response = await fetch(`/api/projects/${id}/remedials/${remedialId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        await fetchRemedials();
        await fetchProject(); // Refresh project to update status
      }
    } catch (error) {
      console.error('Error deleting remedial:', error);
    }
  };

  // Save order item
  const saveOrderItem = async (item: OrderItem, index: number) => {
    try {
      setSaving(true);
      
      if (item.isNew && !item.id) {
        // Create new item
        const response = await axios.post(
          `${API_BASE_URL}/projects/${id}/order-items`,
          {
            supplier_id: item.supplier_id,
            qty: item.qty,
            hxw: item.hxw,
            type: item.type,
            l: item.l,
            drillings: item.drillings,
            r: item.r,
            sort_order: index
          },
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        );
        
        // Update item with ID from server
        const updatedItems = [...orderItems];
        updatedItems[index] = { ...response.data, isNew: false };
        setOrderItems(updatedItems);
        
      } else if (item.id) {
        // Update existing item
        await axios.put(
          `${API_BASE_URL}/projects/${id}/order-items/${item.id}`,
          {
            supplier_id: item.supplier_id,
            qty: item.qty,
            hxw: item.hxw,
            type: item.type,
            l: item.l,
            drillings: item.drillings,
            r: item.r,
            sort_order: index
          },
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        );
      }
      
    } catch (error) {
      console.error('Error saving order item:', error);
    } finally {
      setSaving(false);
    }
  };

  // Handle field change with auto-save
  const handleItemChange = (index: number, field: keyof OrderItem, value: any) => {
    const updatedItems = [...orderItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setOrderItems(updatedItems);
    
    // Auto-save after 1 second of inactivity
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }
    
    const timeout = setTimeout(() => {
      saveOrderItem(updatedItems[index], index);
    }, 1000);
    setAutoSaveTimeout(timeout);
  };

  // Add new row
  const addNewRow = () => {
    const newItem: OrderItem = {
      qty: '',
      hxw: '',
      type: '',
      l: '',
      drillings: '',
      r: '',
      isNew: true,
      sort_order: orderItems.length
    };
    setOrderItems([...orderItems, newItem]);
  };

  // Delete row
  const deleteRow = async (index: number) => {
    const item = orderItems[index];
    
    if (item.id) {
      try {
        await axios.delete(
          `${API_BASE_URL}/projects/${id}/order-items/${item.id}`,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        );
      } catch (error) {
        console.error('Error deleting order item:', error);
        return;
      }
    }
    
    const updatedItems = orderItems.filter((_, i) => i !== index);
    setOrderItems(updatedItems);
  };

  // Fetch supplier order preview
  const fetchSupplierOrderPreview = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/projects/${id}/supplier-order-preview`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSupplierOrderOptions(data.suppliers || []);
        // Select all by default
        setSelectedSupplierKeys(data.suppliers.map((s: any) => s.key));
      }
    } catch (error) {
      console.error('Error fetching supplier preview:', error);
    }
  };

  // Handle Issue Orders for Project
  const handleIssueOrders = async (selectedSuppliers?: string[]) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/projects/${id}/issue-orders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          orderItems: orderItems,
          selectedSuppliers: selectedSuppliers || undefined
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Orders issued:', data);

        // Report what was ACTUALLY created, and surface any suppliers that failed
        const created = data.documentsCreated || 0;
        const failed = data.failed || [];

        if (failed.length > 0) {
          const names = failed.map((f: any) => f.supplier).join(', ');
          setSnackbarSeverity('error');
          setSnackbarMessage(
            `Failed to create ${failed.length} of ${created + failed.length} supplier order(s): ${names}. ` +
            `Reason: ${failed[0].reason}`
          );
          setSnackbarOpen(true);
        } else if (created > 0) {
          setSnackbarSeverity('success');
          setSnackbarMessage(`Order forms have been generated for ${created} supplier(s)`);
          setSnackbarOpen(true);
        }

        // Reload documents to show new files
        await fetchDocuments();
      } else {
        const errorData = await response.json();
        console.error('Failed to issue orders:', errorData);
        setError(errorData.message || 'Failed to issue orders');
      }
    } catch (error) {
      console.error('Error issuing orders:', error);
      setError('Error issuing orders');
    } finally {
      setLoading(false);
    }
  };

  const handleIssueRemedialOrders = async (remedialId: number) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/remedials/${remedialId}/issue-orders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Remedial orders issued:', data);

        const created = data.documentsCreated || 0;
        const failed = data.failed || [];

        if (failed.length > 0) {
          const names = failed.map((f: any) => f.supplier).join(', ');
          setSnackbarSeverity('error');
          setSnackbarMessage(
            `Failed to create ${failed.length} of ${created + failed.length} remedial order(s): ${names}. ` +
            `Reason: ${failed[0].reason}`
          );
          setSnackbarOpen(true);
        } else if (created > 0) {
          setSnackbarSeverity('success');
          setSnackbarMessage(`Remedial order forms have been generated for ${created} supplier(s)`);
          setSnackbarOpen(true);
        }

        // Reload documents to show new files
        await fetchDocuments();
      } else {
        const errorData = await response.json();
        console.error('Failed to issue remedial orders:', errorData);
        setError(errorData.message || 'Failed to issue remedial orders');
      }
    } catch (error) {
      console.error('Error issuing remedial orders:', error);
      setError('Error issuing remedial orders');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCustomerInvoice = async () => {
    try {
      setCreatingInvoice(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`/api/projects/${id}/create-customer-invoice`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          orderItems: orderItems
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Customer invoice created successfully:', data);

        setSnackbarMessage('Customer invoice has been generated and saved');
        setSnackbarOpen(true);

        // Reload documents and invoices to show new invoice
        await fetchDocuments();
        await fetchInvoices();
      } else {
        const errorData = await response.json();
        console.error('Failed to create customer invoice:', errorData);

        // Show user-friendly error message
        alert(errorData.error || 'Failed to create customer invoice');
      }
    } catch (error) {
      console.error('Error creating customer invoice:', error);
      alert('Error creating customer invoice. Please try again.');
    } finally {
      setCreatingInvoice(false);
    }
  };

  // Helper function to safely format currency
  const formatCurrency = (amount: any): string => {
    const num = typeof amount === 'number' ? amount : parseFloat(amount);
    return isNaN(num) ? '0.00' : num.toFixed(2);
  };

  // Calculate project cost from material cost only
  const calculateProjectCost = () => {
    if (!project) return 0;
    const materialCost = parseFloat(project.material_cost as any) || 0;
    return materialCost;
  };

  // Calculate balance
  const calculateBalance = () => {
    if (!project) return 0;
    const projectCost = calculateProjectCost();
    const deposit = parseFloat(project.deposit_amount as any) || 0;
    const totalPayments = payments.reduce((sum, p) => sum + (parseFloat(p.amount as any) || 0), 0);
    const totalPaid = deposit + totalPayments;
    return projectCost - totalPaid;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <Typography>Loading project...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">Error: {error}</Typography>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Back
        </Button>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box p={3}>
        <Typography>Project not found</Typography>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Back
        </Button>
      </Box>
    );
  }

  // Add error boundary for render
  try {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
        <Box
          p={3}
          sx={isTablet ? {
            // Tablet performance optimizations - remove shadows and reduce effects
            '& .MuiCard-root': {
              boxShadow: 'none',
              border: '1px solid rgba(0, 0, 0, 0.12)',
            },
            '& .MuiPaper-root': {
              boxShadow: 'none',
            },
            '& .MuiAccordion-root': {
              boxShadow: 'none',
              border: '1px solid rgba(0, 0, 0, 0.12)',
            },
            '& *': {
              transition: 'none !important',
            },
            '& .MuiButton-root:hover': {
              boxShadow: 'none',
            },
            '& .MuiIconButton-root:hover': {
              backgroundColor: 'transparent',
            },
            // Input optimizations for tablets
            '& .MuiInputBase-root': {
              transition: 'none !important',
            },
            '& .MuiOutlinedInput-root': {
              transition: 'none !important',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0, 0, 0, 0.23)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderWidth: '1px',
              },
            },
            '& .MuiInputLabel-root': {
              transition: 'none !important',
            },
            '& .MuiTouchRipple-root': {
              display: 'none',
            },
          } : {}}
        >
        {/* Header with buttons */}
        <Box
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          gap={2}
          mb={3}
        >
          {/* Back button - Left side */}
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{
              alignSelf: { xs: 'flex-start', md: 'center' },
              flexShrink: 0
            }}
          >
            Back
          </Button>

          {/* Action buttons - Right side */}
          <Box
            display="flex"
            gap={1.5}
            flexWrap="wrap"
            justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
          >
            <Button
              variant="contained"
              startIcon={<PhotoCamera />}
              onClick={() => setShowPicturesUpload(true)}
              disabled={project?.status === 'remedial'}
              sx={{
                minHeight: 40,
                whiteSpace: 'nowrap',
                px: { xs: 2, sm: 3 },
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}
            >
              Add Picture
            </Button>
            <Button
              variant="contained"
              startIcon={<Description />}
              onClick={() => setShowDocumentUpload(true)}
              disabled={project?.status === 'remedial'}
              sx={{
                minHeight: 40,
                whiteSpace: 'nowrap',
                px: { xs: 2, sm: 3 },
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}
            >
              Add Document
            </Button>
            <Button
              variant="contained"
              startIcon={<Article />}
              onClick={handleCreateLetter}
              disabled={project?.status === 'remedial'}
              sx={{
                minHeight: 40,
                whiteSpace: 'nowrap',
                px: { xs: 2, sm: 3 },
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}
            >
              Create D & F Letter
            </Button>
            <Button
              variant="contained"
              startIcon={<LocalShipping />}
              onClick={handleCreateDelivery}
              disabled={project?.status === 'remedial'}
              sx={{
                minHeight: 40,
                whiteSpace: 'nowrap',
                px: { xs: 2, sm: 3 },
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}
            >
              Create Delivery
            </Button>
          </Box>
        </Box>

        {/* Single Column Layout */}
        <Box display="flex" flexDirection="column" gap={3}>
            {/* Customer Information */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Customer Information
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
                  {/* Left side - Customer details and address fields */}
                  <Box sx={{ flex: '0 0 50%', display: 'flex', flexDirection: 'column' }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Person fontSize="small" color="action" />
                      <Typography>{project?.customer_name || ''}</Typography>
                    </Box>
                    {project?.customer_email && (
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Email fontSize="small" color="action" />
                        <Typography variant="body2">{project?.customer_email}</Typography>
                      </Box>
                    )}
                    {project?.customer_phone && (
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Phone fontSize="small" color="action" />
                        <Typography variant="body2">{project?.customer_phone}</Typography>
                      </Box>
                    )}
                    {project?.customer_home_phone && (
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Phone fontSize="small" color="action" />
                        <Typography variant="body2">{project?.customer_home_phone} (Home)</Typography>
                      </Box>
                    )}

                    {/* Address Section */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 1, gap: 1 }}>
                      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOn fontSize="small" color="primary" />
                        Address
                      </Typography>
                      <Tooltip title="Search via Postcode" arrow>
                        <IconButton
                          onClick={handleOpenPostcodeLookup}
                          size="small"
                          sx={{ color: 'primary.main' }}
                          disabled={project?.status === 'remedial'}
                        >
                          <Search fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Address Line 1"
                        value={project?.customer_address_1 || ''}
                        onChange={(e) => project && setProject({ ...project, customer_address_1: e.target.value })}
                        onBlur={handleAddressFieldBlur}
                        disabled={project?.status === 'remedial'}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Address Line 2"
                        value={project?.customer_address_2 || ''}
                        onChange={(e) => project && setProject({ ...project, customer_address_2: e.target.value })}
                        onBlur={handleAddressFieldBlur}
                        disabled={project?.status === 'remedial'}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Address Line 3"
                        value={project?.customer_address_3 || ''}
                        onChange={(e) => project && setProject({ ...project, customer_address_3: e.target.value })}
                        onBlur={handleAddressFieldBlur}
                        disabled={project?.status === 'remedial'}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Town/City"
                        value={project?.customer_town || ''}
                        onChange={(e) => project && setProject({ ...project, customer_town: e.target.value })}
                        onBlur={handleAddressFieldBlur}
                        disabled={project?.status === 'remedial'}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Postcode"
                        value={project?.customer_postcode || ''}
                        onChange={(e) => project && setProject({ ...project, customer_postcode: e.target.value })}
                        onBlur={handleAddressFieldBlur}
                        disabled={project?.status === 'remedial'}
                      />
                    </Box>
                  </Box>

                  {/* Right side - Map spanning full height */}
                  {mapCoordinates && (
                    <Box sx={{
                      flex: 1,
                      minHeight: 350,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid rgba(0, 0, 0, 0.12)'
                    }}>
                      {/* On tablets, show button to load map for better performance */}
                      {isTablet && !mapLoaded ? (
                        <Box sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#f5f5f5'
                        }}>
                          <LocationOn sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                          <Button
                            variant="contained"
                            startIcon={<LocationOn />}
                            onClick={() => setMapLoaded(true)}
                          >
                            Show Map
                          </Button>
                        </Box>
                      ) : (
                        <MapContainer
                          key={`${mapCoordinates[0]}-${mapCoordinates[1]}`}
                          center={mapCoordinates}
                          zoom={15}
                          style={{ height: '100%', width: '100%' }}
                          scrollWheelZoom={false}
                        >
                          <TileLayer
                            attribution='Tiles &copy; <a href="https://www.esri.com/">Esri</a> &mdash; Source: Esri, HERE, Garmin, USGS, NGA'
                            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
                          />
                          <Marker position={mapCoordinates}>
                            <Popup>
                              {project?.customer_name || ''}<br />
                              {[project?.customer_address_1, project?.customer_address_2, project?.customer_town, project?.customer_postcode].filter(Boolean).join(', ')}
                            </Popup>
                          </Marker>
                        </MapContainer>
                      )}
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Project Information */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Project Information
                </Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box>
                    <TextField
                      fullWidth
                      label="Project Name"
                      value={project?.title || ''}
                      onChange={(e) => project && setProject({ ...project, title: e.target.value })}
                      onBlur={(e) => handleFieldBlur('title', e.target.value)}
                      disabled={project?.status === 'remedial'}
                    />
                  </Box>
                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Description"
                      value={project?.description || ''}
                      onChange={(e) => project && setProject({ ...project, description: e.target.value })}
                      onBlur={(e) => handleFieldBlur('description', e.target.value)}
                      disabled={project?.status === 'remedial'}
                    />
                  </Box>
                  <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
                    <Box flex={1}>
                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={project?.status || 'planning'}
                          label="Status"
                          onChange={(e) => {
                            const newStatus = e.target.value as Project['status'];
                            if (project) setProject({ ...project, status: newStatus });
                            autoSaveField('status', newStatus);
                          }}
                        >
                          <MenuItem value="planning">Planning</MenuItem>
                          <MenuItem value="delivery_ready">Delivery Ready</MenuItem>
                          <MenuItem value="installation">Installation</MenuItem>
                          <MenuItem value="completed">Completed</MenuItem>
                          <MenuItem value="remedial">Remedial</MenuItem>
                          <MenuItem value="remedial_resolved">Remedial Resolved</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    <Box flex={1}>
                      <TextField
                        fullWidth
                        label="Order Number"
                        value={project?.order_number || ''}
                        onChange={(e) => project && setProject({ ...project, order_number: e.target.value })}
                        onBlur={(e) => handleFieldBlur('order_number', e.target.value)}
                        disabled={project?.status === 'remedial'}
                      />
                    </Box>
                  </Box>
                  {/* Temporarily commented out for debugging
                  <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
                    <Box flex={1}>
                      <DatePicker
                        label="Start Date"
                        value={parseProjectDate(project.start_date)}
                        onChange={(date) => handleDateChange('start_date', date)}
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                          },
                        }}
                      />
                    </Box>
                    <Box flex={1}>
                      <DatePicker
                        label="End Date"
                        value={parseProjectDate(project.end_date)}
                        onChange={(date) => handleDateChange('end_date', date)}
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                          },
                        }}
                      />
                    </Box>
                    <Box flex={1}>
                      <DatePicker
                        label="Supplier delivery date"
                        value={parseProjectDate(project.delivery_date)}
                        onChange={(date) => handleDateChange('delivery_date', date)}
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                  */}
                  <Box>
                    <TextField
                      fullWidth
                      label="Nice Name (For Documents)"
                      placeholder="Optional: Override project title on documents"
                      value={project?.nice_name || ''}
                      onChange={(e) => project && setProject({ ...project, nice_name: e.target.value })}
                      onBlur={(e) => handleFieldBlur('nice_name', e.target.value)}
                      helperText="This name will be used instead of the project title on documents if provided"
                      disabled={project?.status === 'remedial'}
                    />
                  </Box>
                  <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
                    <Box flex={1}>
                      <TextField
                        fullWidth
                        label="Project Start Date"
                        type="date"
                        value={project?.start_date ? project.start_date.split('T')[0] : ''}
                        onChange={(e) => project && setProject({ ...project, start_date: e.target.value })}
                        onBlur={(e) => handleFieldBlur('start_date', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        disabled={project?.status === 'remedial'}
                      />
                    </Box>
                    <Box flex={1}>
                      <TextField
                        fullWidth
                        label="Project End Date"
                        type="date"
                        value={project?.end_date ? project.end_date.split('T')[0] : ''}
                        onChange={(e) => project && setProject({ ...project, end_date: e.target.value })}
                        onBlur={(e) => handleFieldBlur('end_date', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        disabled={project?.status === 'remedial'}
                      />
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Remedial Issues Section - Show when status is remedial or remedial_resolved */}
            {(project?.status === 'remedial' || project?.status === 'remedial_resolved') && (
              <Card sx={{ mb: 3, border: '2px solid #f44336' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#f44336', display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Build color="error" />
                      Remedial Issues
                    </Box>
                    <Typography variant="caption" sx={{ fontWeight: 'normal' }}>
                      {remedials.filter(r => r.status === 'open').length} Open, {remedials.filter(r => r.status === 'resolved').length} Resolved
                    </Typography>
                  </Typography>

                  {/* List of Remedial Issues */}
                  {remedials.length > 0 ? (
                    <Box display="flex" flexDirection="column" gap={2} mb={3}>
                      {remedials.map((remedial, index) => (
                        <Accordion
                          key={remedial.id}
                          sx={{
                            border: remedial.status === 'open' ? '1px solid #f44336' : '1px solid #4caf50',
                            backgroundColor: remedial.status === 'open' ? '#fff5f5' : '#f1f8f4',
                            '&:before': { display: 'none' }
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMore />}
                            sx={{
                              '& .MuiAccordionSummary-content': {
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                overflow: 'hidden'
                              }
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                {remedial.status === 'open' ? '⚠️' : '✅'} Issue #{index + 1}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  minWidth: 0,
                                  flex: 1
                                }}
                              >
                                {remedial.repair && `- ${remedial.repair}`}
                              </Typography>
                            </Box>
                            <Box display="flex" gap={1} sx={{ flexShrink: 0 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/remedials/${remedial.id}/order-build`);
                                }}
                                sx={{ minWidth: 'auto', flexShrink: 0 }}
                              >
                                Build Order
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  remedial.id && handleIssueRemedialOrders(remedial.id);
                                }}
                                sx={{ minWidth: 'auto', flexShrink: 0 }}
                              >
                                Create Order
                              </Button>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  remedial.id && setDeleteRemedialId(remedial.id);
                                }}
                                sx={{ color: '#f44336', flexShrink: 0 }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>

                            <Box display="flex" flexDirection="column" gap={2}>
                              <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
                                <Box flex={1}>
                                  <TextField
                                    fullWidth
                                    label="Status"
                                    select
                                    value={remedial.status}
                                    onChange={(e) => remedial.id && handleUpdateRemedial(remedial.id, { status: e.target.value as 'open' | 'resolved' })}
                                  >
                                    <MenuItem value="open">Open</MenuItem>
                                    <MenuItem value="resolved">Resolved</MenuItem>
                                  </TextField>
                                </Box>
                                <Box flex={1}>
                                  <TextField
                                    fullWidth
                                    label="Reported Date"
                                    type="date"
                                    key={`reported-date-${remedial.id}-${remedial.reported_date}`}
                                    defaultValue={remedial.reported_date ? remedial.reported_date.split('T')[0] : ''}
                                    onBlur={(e) => remedial.id && handleUpdateRemedial(remedial.id, { reported_date: e.target.value })}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Box>
                                <Box flex={1}>
                                  <TextField
                                    fullWidth
                                    label="Fit Date"
                                    type="date"
                                    key={`start-date-${remedial.id}-${remedial.start_date}`}
                                    defaultValue={remedial.start_date ? remedial.start_date.split('T')[0] : ''}
                                    onBlur={(e) => remedial.id && handleUpdateRemedial(remedial.id, { start_date: e.target.value })}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Box>
                                <Box flex={1}>
                                  <TextField
                                    fullWidth
                                    label="Resolved Date"
                                    type="date"
                                    key={`resolved-date-${remedial.id}-${remedial.resolved_date}`}
                                    defaultValue={remedial.resolved_date ? remedial.resolved_date.split('T')[0] : ''}
                                    onBlur={(e) => remedial.id && handleUpdateRemedial(remedial.id, { resolved_date: e.target.value })}
                                    InputLabelProps={{ shrink: true }}
                                    disabled={remedial.status === 'open'}
                                  />
                                </Box>
                              </Box>

                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Repair"
                                key={`repair-${remedial.id}-${remedial.repair}`}
                                defaultValue={remedial.repair || ''}
                                onBlur={(e) => remedial.id && handleUpdateRemedial(remedial.id, { repair: e.target.value })}
                                placeholder="Describe the repair needed..."
                              />

                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Further Info"
                                key={`description-${remedial.id}-${remedial.description}`}
                                defaultValue={remedial.description || ''}
                                onBlur={(e) => remedial.id && handleUpdateRemedial(remedial.id, { description: e.target.value })}
                                placeholder="Additional information..."
                              />

                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Location of Materials"
                                key={`location-${remedial.id}-${remedial.location_of_materials}`}
                                defaultValue={remedial.location_of_materials || ''}
                                onBlur={(e) => remedial.id && handleUpdateRemedial(remedial.id, { location_of_materials: e.target.value })}
                                placeholder="Where materials are located..."
                              />

                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Contact Made/Confirmed with Customer/Notes for Alan"
                                key={`contact-${remedial.id}-${remedial.customer_contact_notes}`}
                                defaultValue={remedial.customer_contact_notes || ''}
                                onBlur={(e) => remedial.id && handleUpdateRemedial(remedial.id, { customer_contact_notes: e.target.value })}
                                placeholder="Customer contact details and notes..."
                              />

                              <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
                                <TextField
                                  fullWidth
                                  label="Date Fitter Invoice Paid"
                                  type="date"
                                  key={`invoice-paid-${remedial.id}-${remedial.date_invoice_paid}`}
                                  defaultValue={remedial.date_invoice_paid ? remedial.date_invoice_paid.split('T')[0] : ''}
                                  onBlur={(e) => remedial.id && handleUpdateRemedial(remedial.id, { date_invoice_paid: e.target.value })}
                                  InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                  fullWidth
                                  label="Date Materials Invoice Paid"
                                  type="date"
                                  key={`materials-invoice-paid-${remedial.id}-${remedial.date_materials_invoice_paid}`}
                                  defaultValue={remedial.date_materials_invoice_paid ? remedial.date_materials_invoice_paid.split('T')[0] : ''}
                                  onBlur={(e) => remedial.id && handleUpdateRemedial(remedial.id, { date_materials_invoice_paid: e.target.value })}
                                  InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                  fullWidth
                                  select
                                  label="Who's at Fault"
                                  value={remedial.whos_at_fault || ''}
                                  onChange={(e) => remedial.id && handleUpdateRemedial(remedial.id, { whos_at_fault: e.target.value })}
                                >
                                  <MenuItem value="">None</MenuItem>
                                  <MenuItem value="TONY">TONY</MenuItem>
                                  <MenuItem value="ALBION">ALBION</MenuItem>
                                  <MenuItem value="PULLMAN">PULLMAN</MenuItem>
                                  <MenuItem value="OTHER">OTHER</MenuItem>
                                  <MenuItem value="FITTING">FITTING</MenuItem>
                                </TextField>
                              </Box>

                              <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
                                <TextField
                                  fullWidth
                                  label="Fitter Cost (£)"
                                  value={remedial.fitter_cost ?? ''}
                                  onChange={(e) => {
                                    if (remedial.id) {
                                      const updatedRemedials = remedials.map(r =>
                                        r.id === remedial.id ? { ...r, fitter_cost: e.target.value as any } : r
                                      );
                                      setRemedials(updatedRemedials);
                                    }
                                  }}
                                  onBlur={(e) => remedial.id && handleUpdateRemedial(remedial.id, { fitter_cost: parseFloat(e.target.value) || 0 })}
                                  placeholder="0.00"
                                />
                                <TextField
                                  fullWidth
                                  label="Delivery Cost (£)"
                                  value={remedial.delivery_cost ?? ''}
                                  onChange={(e) => {
                                    if (remedial.id) {
                                      const updatedRemedials = remedials.map(r =>
                                        r.id === remedial.id ? { ...r, delivery_cost: e.target.value as any } : r
                                      );
                                      setRemedials(updatedRemedials);
                                    }
                                  }}
                                  onBlur={(e) => remedial.id && handleUpdateRemedial(remedial.id, { delivery_cost: parseFloat(e.target.value) || 0 })}
                                  placeholder="0.00"
                                />
                                <TextField
                                  fullWidth
                                  label="Material Cost (£)"
                                  value={remedial.material_cost ?? ''}
                                  onChange={(e) => {
                                    if (remedial.id) {
                                      const updatedRemedials = remedials.map(r =>
                                        r.id === remedial.id ? { ...r, material_cost: e.target.value as any } : r
                                      );
                                      setRemedials(updatedRemedials);
                                    }
                                  }}
                                  onBlur={(e) => remedial.id && handleUpdateRemedial(remedial.id, { material_cost: parseFloat(e.target.value) || 0 })}
                                  placeholder="0.00"
                                />
                                <TextField
                                  fullWidth
                                  label="Total Cost"
                                  value={
                                    (Number(remedial.fitter_cost) || 0) +
                                    (Number(remedial.delivery_cost) || 0) +
                                    (Number(remedial.material_cost) || 0)
                                  }
                                  disabled
                                  InputProps={{
                                    startAdornment: <Typography sx={{ mr: 1 }}>£</Typography>
                                  }}
                                  sx={{
                                    '& .MuiInputBase-input.Mui-disabled': {
                                      color: 'rgba(0, 0, 0, 0.87)',
                                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)'
                                    }
                                  }}
                                />
                              </Box>

                              {/* Remedial Order Details */}
                              {remedial.id && remedialOrderItems.get(remedial.id) && remedialOrderItems.get(remedial.id)!.length > 0 && (
                                <Box sx={{ mt: 3 }}>
                                  <Divider sx={{ mb: 2 }} />
                                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                                    Order Details
                                  </Typography>

                                  {/* Order Metadata Fields - Read Only */}
                                  <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                    <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={2}>
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">Customer delivery date</Typography>
                                        <Typography variant="body2">
                                          {remedial.customer_delivery_date ? new Date(remedial.customer_delivery_date).toLocaleDateString('en-GB') : '-'}
                                        </Typography>
                                      </Box>
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">Door Style</Typography>
                                        <Typography variant="body2">{remedial.door_style || '-'}</Typography>
                                      </Box>
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">Worktop Style</Typography>
                                        <Typography variant="body2">{remedial.worktop_style || '-'}</Typography>
                                      </Box>
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">Worktop Make</Typography>
                                        <Typography variant="body2">{remedial.worktop_make || '-'}</Typography>
                                      </Box>
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">Worktop Manufacture</Typography>
                                        <Typography variant="body2">{remedial.worktop_manufacture || '-'}</Typography>
                                      </Box>
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">Worktop Thickness</Typography>
                                        <Typography variant="body2">{remedial.worktop_thickness || '-'}</Typography>
                                      </Box>
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">Worktop Size</Typography>
                                        <Typography variant="body2">{remedial.worktop_size || '-'}</Typography>
                                      </Box>
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">Door Colour</Typography>
                                        <Typography variant="body2">{remedial.item_colour || '-'}</Typography>
                                      </Box>
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">Door Back Colour</Typography>
                                        <Typography variant="body2">{remedial.back_colour || '-'}</Typography>
                                      </Box>
                                    </Box>
                                  </Box>

                                  <TableContainer component={Paper}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                          <TableCell align="center" sx={{ width: '180px', fontWeight: 'bold' }}>Supplier</TableCell>
                                          <TableCell align="center" sx={{ width: '140px', fontWeight: 'bold' }}>Category</TableCell>
                                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Details</TableCell>
                                          <TableCell align="center" sx={{ width: '100px', fontWeight: 'bold' }}>Code</TableCell>
                                          <TableCell align="center" sx={{ width: '100px', fontWeight: 'bold' }}>Supplier Del.</TableCell>
                                          <TableCell align="center" sx={{ width: '60px', fontWeight: 'bold' }}>Note</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {remedialOrderItems.get(remedial.id)!.map((item, index) => {
                                          const isWorktop = item.category_name === 'Worktops';
                                          const isDoors = item.category_name === 'Doors';

                                          return (
                                            <TableRow key={item.id || `remedial-${remedial.id}-${index}`}>
                                              <TableCell align="center">
                                                {item.supplier_name || '-'}
                                              </TableCell>
                                              <TableCell align="center">
                                                {item.category_name || '-'}
                                              </TableCell>
                                              <TableCell>
                                                {isWorktop ? (
                                                  // Worktops: Qty, Description, SQ, Colour
                                                  <Box display="flex" gap={3} alignItems="flex-start">
                                                    <Box>
                                                      <Typography variant="caption" color="text.secondary">Qty</Typography>
                                                      <Typography variant="body2">{item.qty || '-'}</Typography>
                                                    </Box>
                                                    <Box>
                                                      <Typography variant="caption" color="text.secondary">Description</Typography>
                                                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{item.description || '-'}</Typography>
                                                    </Box>
                                                    <Box>
                                                      <Typography variant="caption" color="text.secondary">SQ</Typography>
                                                      <Typography variant="body2">{item.sq || '-'}</Typography>
                                                    </Box>
                                                    <Box>
                                                      <Typography variant="caption" color="text.secondary">Colour</Typography>
                                                      <Typography variant="body2">{item.colour || '-'}</Typography>
                                                    </Box>
                                                  </Box>
                                                ) : isDoors ? (
                                                  // Doors: Qty, HxW, Type, L, Drillings, R
                                                  <Box display="flex" gap={3} alignItems="flex-start">
                                                    <Box>
                                                      <Typography variant="caption" color="text.secondary">Qty</Typography>
                                                      <Typography variant="body2">{item.qty || '-'}</Typography>
                                                    </Box>
                                                    <Box>
                                                      <Typography variant="caption" color="text.secondary">HxW</Typography>
                                                      <Typography variant="body2">{item.hxw || '-'}</Typography>
                                                    </Box>
                                                    <Box>
                                                      <Typography variant="caption" color="text.secondary">Type</Typography>
                                                      <Typography variant="body2">{item.type || '-'}</Typography>
                                                    </Box>
                                                    <Box>
                                                      <Typography variant="caption" color="text.secondary">L</Typography>
                                                      <Typography variant="body2">{item.l || '-'}</Typography>
                                                    </Box>
                                                    <Box>
                                                      <Typography variant="caption" color="text.secondary">Drillings</Typography>
                                                      <Typography variant="body2">{item.drillings || '-'}</Typography>
                                                    </Box>
                                                    <Box>
                                                      <Typography variant="caption" color="text.secondary">R</Typography>
                                                      <Typography variant="body2">{item.r || '-'}</Typography>
                                                    </Box>
                                                  </Box>
                                                ) : (
                                                  // Others: Qty, Description, Code
                                                  <Box display="flex" gap={3} alignItems="flex-start">
                                                    <Box>
                                                      <Typography variant="caption" color="text.secondary">Qty</Typography>
                                                      <Typography variant="body2">{item.qty || '-'}</Typography>
                                                    </Box>
                                                    <Box flex={1}>
                                                      <Typography variant="caption" color="text.secondary">Description</Typography>
                                                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{item.description || '-'}</Typography>
                                                    </Box>
                                                  </Box>
                                                )}
                                              </TableCell>
                                              <TableCell align="center">
                                                {item.code || '-'}
                                              </TableCell>
                                              <TableCell align="center">
                                                {item.supplier_delivery_date ? new Date(item.supplier_delivery_date).toLocaleDateString('en-GB') : '-'}
                                              </TableCell>
                                              <TableCell align="center">
                                                {item.note_ref ? `*${item.note_ref}` : '-'}
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </Box>
                              )}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      No remedial issues recorded yet.
                    </Typography>
                  )}

                  {/* Add New Remedial Issue */}
                  <Divider sx={{ my: 2 }} />

                  {!showAddRemedialForm ? (
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => setShowAddRemedialForm(true)}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      Add New Remedial Issue
                    </Button>
                  ) : (
                    <>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Add New Remedial Issue
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={2}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Repair"
                      value={newRemedial.repair || ''}
                      onChange={(e) => { setNewRemedial({ ...newRemedial, repair: e.target.value }); checkFieldForIssues('Repair', e.target.value); }}
                      onPaste={handleRemedialPaste('repair', 'Repair')}
                      placeholder="Describe the repair needed..."
                      error={!!fieldIssues['Repair']}
                      helperText={fieldIssues['Repair'] ? `⚠️ Contains: ${fieldIssues['Repair']}` : undefined}
                    />

                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Further Info"
                      value={newRemedial.description}
                      onChange={(e) => { setNewRemedial({ ...newRemedial, description: e.target.value }); checkFieldForIssues('Further Info', e.target.value); }}
                      onPaste={handleRemedialPaste('description', 'Further Info')}
                      placeholder="Additional information..."
                      error={!!fieldIssues['Further Info']}
                      helperText={fieldIssues['Further Info'] ? `⚠️ Contains: ${fieldIssues['Further Info']}` : undefined}
                    />

                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Location of Materials"
                      value={newRemedial.location_of_materials || ''}
                      onChange={(e) => { setNewRemedial({ ...newRemedial, location_of_materials: e.target.value }); checkFieldForIssues('Location of Materials', e.target.value); }}
                      onPaste={handleRemedialPaste('location_of_materials', 'Location of Materials')}
                      placeholder="Where materials are located..."
                      error={!!fieldIssues['Location of Materials']}
                      helperText={fieldIssues['Location of Materials'] ? `⚠️ Contains: ${fieldIssues['Location of Materials']}` : undefined}
                    />

                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Contact Made/Confirmed with Customer/Notes for Alan"
                      value={newRemedial.customer_contact_notes || ''}
                      onChange={(e) => { setNewRemedial({ ...newRemedial, customer_contact_notes: e.target.value }); checkFieldForIssues('Contact/Notes', e.target.value); }}
                      onPaste={handleRemedialPaste('customer_contact_notes', 'Contact/Notes')}
                      placeholder="Customer contact details and notes..."
                      error={!!fieldIssues['Contact/Notes']}
                      helperText={fieldIssues['Contact/Notes'] ? `⚠️ Contains: ${fieldIssues['Contact/Notes']}` : undefined}
                    />

                    <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
                      <Box flex={1}>
                        <TextField
                          fullWidth
                          label="Status"
                          select
                          value={newRemedial.status}
                          onChange={(e) => setNewRemedial({ ...newRemedial, status: e.target.value as 'open' | 'resolved' })}
                        >
                          <MenuItem value="open">Open</MenuItem>
                          <MenuItem value="resolved">Resolved</MenuItem>
                        </TextField>
                      </Box>
                      <Box flex={1}>
                        <TextField
                          fullWidth
                          label="Reported Date"
                          type="date"
                          value={newRemedial.reported_date || ''}
                          onChange={(e) => setNewRemedial({ ...newRemedial, reported_date: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                      <Box flex={1}>
                        <TextField
                          fullWidth
                          label="Fit Date"
                          type="date"
                          value={newRemedial.start_date || ''}
                          onChange={(e) => setNewRemedial({ ...newRemedial, start_date: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                    </Box>

                    <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
                      <TextField
                        fullWidth
                        label="Date Fitter Invoice Paid"
                        type="date"
                        value={newRemedial.date_invoice_paid || ''}
                        onChange={(e) => setNewRemedial({ ...newRemedial, date_invoice_paid: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        fullWidth
                        label="Date Materials Invoice Paid"
                        type="date"
                        value={newRemedial.date_materials_invoice_paid || ''}
                        onChange={(e) => setNewRemedial({ ...newRemedial, date_materials_invoice_paid: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        fullWidth
                        select
                        label="Who's at Fault"
                        value={newRemedial.whos_at_fault || ''}
                        onChange={(e) => setNewRemedial({ ...newRemedial, whos_at_fault: e.target.value })}
                      >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="TONY">TONY</MenuItem>
                        <MenuItem value="ALBION">ALBION</MenuItem>
                        <MenuItem value="PULLMAN">PULLMAN</MenuItem>
                        <MenuItem value="OTHER">OTHER</MenuItem>
                        <MenuItem value="FITTING">FITTING</MenuItem>
                      </TextField>
                    </Box>

                    <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
                      <TextField
                        fullWidth
                        label="Fitter Cost (£)"
                        value={newRemedial.fitter_cost ?? ''}
                        onChange={(e) => setNewRemedial({ ...newRemedial, fitter_cost: e.target.value as any })}
                        placeholder="0.00"
                      />
                      <TextField
                        fullWidth
                        label="Delivery Cost (£)"
                        value={newRemedial.delivery_cost ?? ''}
                        onChange={(e) => setNewRemedial({ ...newRemedial, delivery_cost: e.target.value as any })}
                        placeholder="0.00"
                      />
                      <TextField
                        fullWidth
                        label="Material Cost (£)"
                        value={newRemedial.material_cost ?? ''}
                        onChange={(e) => setNewRemedial({ ...newRemedial, material_cost: e.target.value as any })}
                        placeholder="0.00"
                      />
                    </Box>

                    {dirtyPasteFields.length > 0 && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        <strong>Formatted text detected in: {dirtyPasteFields.join(', ')}</strong>
                        <br />
                        Formatting has been automatically stripped. Check the {dirtyPasteFields.length === 1 ? 'field' : 'fields'} look correct before saving.
                      </Alert>
                    )}
                    {Object.keys(fieldIssues).length > 0 && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        <strong>Cannot save — problematic characters found:</strong>
                        <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
                          {Object.entries(fieldIssues).map(([field, issue]) => (
                            <li key={field}><strong>{field}</strong>: {issue}</li>
                          ))}
                        </ul>
                        Please delete and retype that text manually rather than copying and pasting.
                      </Alert>
                    )}
                    {remedialError && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        {remedialError}
                      </Alert>
                    )}
                    <Box display="flex" gap={2}>
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleAddRemedial}
                        disabled={!newRemedial.description}
                      >
                        Add Remedial Issue
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => { setShowAddRemedialForm(false); setRemedialError(null); setDirtyPasteFields([]); setFieldIssues({}); }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Notes & Comments from Lead */}
            {project?.lead_id && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={notesExpanded ? 2 : 0}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Notes color="primary" />
                      Notes & Comments
                    </Typography>
                    <IconButton
                      onClick={() => setNotesExpanded(!notesExpanded)}
                      size="small"
                    >
                      {notesExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>
                  <Collapse in={notesExpanded}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Notes"
                      value={leadNotes === null || leadNotes === undefined ? '' : leadNotes}
                      onChange={(e) => {
                        const value = e.target.value;
                        setLeadNotes(value === null || value === undefined ? '' : value);
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        updateLeadNotes(value === null || value === undefined ? '' : value);
                      }}
                      placeholder="Add any additional notes or comments about this lead..."
                      helperText="These notes are linked to the original lead"
                      disabled={project?.status === 'remedial'}
                    />
                  </Collapse>
                </CardContent>
              </Card>
            )}


            {/* Order Details */}
            <Box>
              <Accordion
                expanded={orderDetailsExpanded}
                onChange={() => setOrderDetailsExpanded(!orderDetailsExpanded)}
                TransitionProps={{ timeout: isTablet ? 0 : 300 }}
                sx={{
                  boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
                  borderRadius: 2,
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  mb: 3,
                  '&:before': { display: 'none' },
                  '&.MuiAccordion-root': {
                    borderRadius: 2
                  },
                  '&.MuiAccordion-root:first-of-type': {
                    borderRadius: 2
                  },
                  '&.MuiAccordion-root:last-of-type': {
                    borderRadius: 2
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h6">
                      Order Details
                    </Typography>
                    <Badge badgeContent={orderItems.filter(item => !(item as any).remedial_id).length} color="primary">
                      <Typography variant="body2" color="text.secondary">
                        {orderItems.filter(item => !(item as any).remedial_id).length} item{orderItems.filter(item => !(item as any).remedial_id).length !== 1 ? 's' : ''}
                      </Typography>
                    </Badge>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mr: 2 }} onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={() => navigate(`/projects/${id}/order-build`)}
                      disabled={project?.status === 'remedial'}
                    >
                      Build Order
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={async () => {
                        setIssueOrderModalOpen(true);
                        await fetchSupplierOrderPreview();
                      }}
                      disabled={project?.status === 'remedial'}
                    >
                      Create Order
                    </Button>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {/* Display project order details */}
                  <Box sx={{ mb: 2, mt: 1, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                      <TextField
                        label="Order Number"
                        value={project?.order_number || ''}
                        onChange={(e) => {
                          if (project) {
                            setProject({ ...project, order_number: e.target.value });
                            autoSaveField('order_number', e.target.value);
                          }
                        }}
                        placeholder="Enter order number"
                        size="small"
                        sx={{ width: '250px' }}
                        disabled={project?.status === 'remedial'}
                      />
                      <TextField
                        label="Total Material Cost"
                        value={project?.material_cost !== null && project?.material_cost !== undefined ?
                          (typeof project.material_cost === 'string' ? project.material_cost : project.material_cost.toString()) : ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (project) {
                            // Allow only numbers and decimal point
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                              // Store as string to prevent formatting during typing
                              setProject({ ...project, material_cost: value as any });
                            }
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          console.log('Material Cost onBlur - raw value:', value);
                          if (project) {
                            const numValue = value && value !== '' ? parseFloat(value) : null;
                            console.log('Material Cost - parsed value:', numValue);
                            setProject({ ...project, material_cost: numValue || undefined });
                            // Save to database
                            console.log('Calling autoSaveField with:', numValue);
                            autoSaveField('material_cost', numValue);
                          }
                        }}
                        placeholder="0.00"
                        size="small"
                        type="text"
                        inputProps={{
                          inputMode: 'decimal',
                          pattern: '[0-9]*\\.?[0-9]*'
                        }}
                        InputProps={{
                          startAdornment: <Typography variant="body2" sx={{ mr: 0.5 }}>£</Typography>,
                        }}
                        sx={{ width: '200px' }}
                        disabled={project?.status === 'remedial'}
                      />
                    </Box>
                    <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={2}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Customer delivery date</Typography>
                        <Typography variant="body2">
                          {project?.customer_delivery_date ? new Date(project.customer_delivery_date).toLocaleDateString('en-GB') : '-'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Door Style</Typography>
                        <Typography variant="body2">{project?.door_style || '-'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Worktop Style</Typography>
                        <Typography variant="body2">{project?.worktop_style || '-'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Worktop Make</Typography>
                        <Typography variant="body2">{project?.worktop_make || '-'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Worktop Manufacture</Typography>
                        <Typography variant="body2">{project?.worktop_manufacture || '-'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Worktop Thickness</Typography>
                        <Typography variant="body2">{project?.worktop_thickness || '-'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Worktop Size</Typography>
                        <Typography variant="body2">{project?.worktop_size || '-'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Door Colour</Typography>
                        <Typography variant="body2">{project?.item_colour || '-'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Door Back Colour</Typography>
                        <Typography variant="body2">{project?.back_colour || '-'}</Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      You can find the Supplier Documents in the Documents section below
                    </Typography>
                  </Box>
                  <TableContainer component={Paper} sx={{ mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell align="center" sx={{ width: '180px', fontWeight: 'bold' }}>Supplier</TableCell>
                          <TableCell align="center" sx={{ width: '140px', fontWeight: 'bold' }}>Category</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Details</TableCell>
                          <TableCell align="center" sx={{ width: '100px', fontWeight: 'bold' }}>Code</TableCell>
                          <TableCell align="center" sx={{ width: '100px', fontWeight: 'bold' }}>Supplier Del.</TableCell>
                          <TableCell align="center" sx={{ width: '60px', fontWeight: 'bold' }}>Note</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {orderItems.filter(item => !(item as any).remedial_id).map((item, index) => {
                          const isWorktop = item.category_name === 'Worktops';
                          const isDoors = item.category_name === 'Doors';

                          return (
                            <TableRow key={item.id || `new-${index}`}>
                              <TableCell align="center">
                                {item.supplier_name || '-'}
                              </TableCell>
                              <TableCell align="center">
                                {item.category_name || '-'}
                              </TableCell>
                              <TableCell>
                                {isWorktop ? (
                                  // Worktops: Qty, Description, SQ, Colour
                                  <Box display="flex" gap={3} alignItems="flex-start">
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Qty</Typography>
                                      <Typography variant="body2">{item.qty || '-'}</Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Description</Typography>
                                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{item.description || '-'}</Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">SQ</Typography>
                                      <Typography variant="body2">{item.sq || '-'}</Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Colour</Typography>
                                      <Typography variant="body2">{item.colour || '-'}</Typography>
                                    </Box>
                                  </Box>
                                ) : isDoors ? (
                                  // Doors: Qty, HxW, Type, L, Drillings, R
                                  <Box display="flex" gap={3} alignItems="flex-start">
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Qty</Typography>
                                      <Typography variant="body2">{item.qty || '-'}</Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">HxW</Typography>
                                      <Typography variant="body2">{item.hxw || '-'}</Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Type</Typography>
                                      <Typography variant="body2">{item.type || '-'}</Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">L</Typography>
                                      <Typography variant="body2">{item.l || '-'}</Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Drillings</Typography>
                                      <Typography variant="body2">{item.drillings || '-'}</Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">R</Typography>
                                      <Typography variant="body2">{item.r || '-'}</Typography>
                                    </Box>
                                  </Box>
                                ) : (
                                  // Other categories (including Accessories): Qty, Description, Style, Colour
                                  <Box display="flex" gap={3} alignItems="flex-start">
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Qty</Typography>
                                      <Typography variant="body2">{item.qty || '-'}</Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Description</Typography>
                                      <Typography variant="body2">{item.description || '-'}</Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Style</Typography>
                                      <Typography variant="body2">{item.style || '-'}</Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Colour</Typography>
                                      <Typography variant="body2">{item.colour || '-'}</Typography>
                                    </Box>
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                {item.code || '-'}
                              </TableCell>
                              <TableCell align="center">
                                {item.supplier_delivery_date ? new Date(item.supplier_delivery_date).toLocaleDateString('en-GB') : '-'}
                              </TableCell>
                              <TableCell align="center">
                                {item.note_ref ? `*${item.note_ref}` : '-'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {orderItems.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ py: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                No order items
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {/* Order Notes (Asterisk References) */}
                  {project?.order_notes_json && (() => {
                    try {
                      const notes = JSON.parse(project.order_notes_json);
                      const noteEntries = Object.entries(notes).filter(([_, value]) => value);
                      if (noteEntries.length === 0) return null;
                      return (
                        <Box sx={{ mt: 3, mb: 2 }}>
                          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Order Notes
                          </Typography>
                          <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                            {noteEntries.map(([num, text]) => (
                              <Typography key={num} variant="body2" sx={{ mb: 0.5 }}>
                                <strong>*{num}:</strong> {text as string}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      );
                    } catch (e) {
                      return null;
                    }
                  })()}

                  {/* Order Detail Notes */}
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Order Detail Notes
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      value={project?.order_detail_notes || ''}
                      onChange={(e) => {
                        if (project) {
                          setProject({ ...project, order_detail_notes: e.target.value });
                          autoSaveField('order_detail_notes', e.target.value);
                        }
                      }}
                      placeholder="Add any additional notes or special instructions for this order..."
                      variant="outlined"
                      sx={{ 
                        backgroundColor: '#fff',
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>

            {/* Order Check List */}
            <Box>
              <Accordion
                expanded={orderChecklistExpanded}
                onChange={() => setOrderChecklistExpanded(!orderChecklistExpanded)}
                TransitionProps={{ timeout: isTablet ? 0 : 300 }}
                sx={{
                  boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
                  borderRadius: 2,
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  mb: 3,
                  backgroundColor: '#fff',
                  '&:before': { display: 'none' },
                  '&.MuiAccordion-root': {
                    borderRadius: 2
                  },
                  '&.MuiAccordion-root:first-of-type': {
                    borderRadius: 2
                  },
                  '&.MuiAccordion-root:last-of-type': {
                    borderRadius: 2
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  aria-controls="order-checklist-content"
                  id="order-checklist-header"
                >
                  <Typography variant="h6">
                    Order Check List
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>
                  {/* Top row of fields */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                      <Box sx={{ minWidth: 150 }}>
                        <DatePicker
                          label="Sold Date"
                          value={orderChecklist?.Sold_date ? parse(orderChecklist.Sold_date, 'dd/MM/yyyy', new Date()) : null}
                          onChange={(newValue) => {
                            if (newValue) {
                              const formattedDate = format(newValue, 'dd/MM/yyyy');
                              setOrderChecklist((prev: any) => ({ ...prev, Sold_date: formattedDate }));
                              autoSaveChecklistField('Sold_date', formattedDate);
                            }
                          }}
                          disabled={project?.status === 'remedial'}
                          format="dd/MM/yyyy"
                          slotProps={{
                            textField: {
                              size: 'small',
                              fullWidth: true,
                              variant: 'outlined'
                            }
                          }}
                        />
                      </Box>
                      <Box sx={{ minWidth: 150 }}>
                        <DatePicker
                          label="Survey Date"
                          value={orderChecklist?.Survey_date ? parse(orderChecklist.Survey_date, 'dd/MM/yyyy', new Date()) : null}
                          onChange={(newValue) => {
                            if (newValue) {
                              const formattedDate = format(newValue, 'dd/MM/yyyy');
                              setOrderChecklist((prev: any) => ({ ...prev, Survey_date: formattedDate }));
                              autoSaveChecklistField('Survey_date', formattedDate);
                            }
                          }}
                          disabled={project?.status === 'remedial'}
                          format="dd/MM/yyyy"
                          slotProps={{
                            textField: {
                              size: 'small',
                              fullWidth: true,
                              variant: 'outlined'
                            }
                          }}
                        />
                      </Box>
                      <Box sx={{ minWidth: 150 }}>
                        <DatePicker
                          label="Fit Date"
                          value={orderChecklist?.fit_date ? parse(orderChecklist.fit_date, 'dd/MM/yyyy', new Date()) : null}
                          onChange={(newValue) => {
                            if (newValue) {
                              const formattedDate = format(newValue, 'dd/MM/yyyy');
                              setOrderChecklist((prev: any) => ({ ...prev, fit_date: formattedDate }));
                              autoSaveChecklistField('fit_date', formattedDate);
                            }
                          }}
                          disabled={project?.status === 'remedial'}
                          format="dd/MM/yyyy"
                          slotProps={{
                            textField: {
                              size: 'small',
                              fullWidth: true,
                              variant: 'outlined'
                            }
                          }}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <FormControl disabled={project?.status === 'remedial'} size="small" sx={{ minWidth: 100 }}>
                        <InputLabel>Gas</InputLabel>
                        <Select
                          value={orderChecklist?.Gas || ''}
                          label="Gas"
                          onChange={(e) => {
                            setOrderChecklist((prev: any) => ({ ...prev, Gas: e.target.value }));
                            autoSaveChecklistField('Gas', e.target.value);
                          }}
                        >
                          <MenuItem value="">-</MenuItem>
                          <MenuItem value="Y">Y</MenuItem>
                          <MenuItem value="N">N</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl disabled={project?.status === 'remedial'} size="small" sx={{ minWidth: 100 }}>
                        <InputLabel>Garage</InputLabel>
                        <Select
                          value={orderChecklist?.Garage || ''}
                          label="Garage"
                          onChange={(e) => {
                            setOrderChecklist((prev: any) => ({ ...prev, Garage: e.target.value }));
                            autoSaveChecklistField('Garage', e.target.value);
                          }}
                        >
                          <MenuItem value="">-</MenuItem>
                          <MenuItem value="Y">Y</MenuItem>
                          <MenuItem value="N">N</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl disabled={project?.status === 'remedial'} size="small" sx={{ minWidth: 100 }}>
                        <InputLabel>Waste</InputLabel>
                        <Select
                          value={orderChecklist?.Waste || ''}
                          label="Waste"
                          onChange={(e) => {
                            setOrderChecklist((prev: any) => ({ ...prev, Waste: e.target.value }));
                            autoSaveChecklistField('Waste', e.target.value);
                          }}
                        >
                          <MenuItem value="">-</MenuItem>
                          <MenuItem value="Y">Y</MenuItem>
                          <MenuItem value="N">N</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl disabled={project?.status === 'remedial'} size="small" sx={{ minWidth: 100 }}>
                        <InputLabel>Tiler</InputLabel>
                        <Select
                          value={orderChecklist?.Tiler || ''}
                          label="Tiler"
                          onChange={(e) => {
                            setOrderChecklist((prev: any) => ({ ...prev, Tiler: e.target.value }));
                            autoSaveChecklistField('Tiler', e.target.value);
                          }}
                        >
                          <MenuItem value="">-</MenuItem>
                          <MenuItem value="Y">Y</MenuItem>
                          <MenuItem value="N">N</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl disabled={project?.status === 'remedial'} size="small" sx={{ minWidth: 100 }}>
                        <InputLabel>Combi</InputLabel>
                        <Select
                          value={orderChecklist?.Combi || ''}
                          label="Combi"
                          onChange={(e) => {
                            setOrderChecklist((prev: any) => ({ ...prev, Combi: e.target.value }));
                            autoSaveChecklistField('Combi', e.target.value);
                          }}
                        >
                          <MenuItem value="">-</MenuItem>
                          <MenuItem value="Y">Y</MenuItem>
                          <MenuItem value="N">N</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl disabled={project?.status === 'remedial'} size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Other Trade</InputLabel>
                        <Select
                          value={orderChecklist?.Other_trade || ''}
                          label="Other Trade"
                          onChange={(e) => {
                            setOrderChecklist((prev: any) => ({ ...prev, Other_trade: e.target.value }));
                            autoSaveChecklistField('Other_trade', e.target.value);
                          }}
                        >
                          <MenuItem value="">-</MenuItem>
                          <MenuItem value="Y">Y</MenuItem>
                          <MenuItem value="N">N</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  {/* Product Table */}
                  <TableContainer component={Paper} sx={{ boxShadow: 0, border: '1px solid #e0e0e0', overflowX: 'auto', overflowY: 'hidden' }}>
                    <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.75rem', padding: '4px 8px' }, minWidth: '100%', tableLayout: 'fixed' }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 'bold', width: '90px' }}>Product Type</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '120px' }}>Supplier</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '170px', whiteSpace: 'normal', wordWrap: 'break-word' }}>Product</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '120px' }}>Order Date</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '110px' }}>Ordered by</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '110px' }}>Checked by</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '120px' }}>Stock Due</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* Doors Row */}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Doors</TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              fullWidth
                              value={orderChecklist?.doors_supplier || ''}
                              onChange={(e) => {
                                setOrderChecklist((prev: any) => ({ ...prev, doors_supplier: e.target.value }));
                                autoSaveChecklistField('doors_supplier', e.target.value);
                              }}
                              displayEmpty
                              sx={{ fontSize: '0.75rem', height: '28px' }}
                            >
                              <MenuItem value=""><em>Select Supplier</em></MenuItem>
                              {suppliers.map((supplier) => (
                                <MenuItem key={supplier.id} value={supplier.company}>
                                  {supplier.company}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            <TextField
                              disabled={project?.status === 'remedial'}
                              size="small"
                              fullWidth
                              multiline
                              minRows={1}
                              maxRows={3}
                              value={orderChecklist?.doors || ''}
                              onChange={(e) => setOrderChecklist((prev: any) => ({ ...prev, doors: e.target.value }))}
                              onBlur={(e) => autoSaveChecklistField('doors', e.target.value)}
                              variant="outlined"
                              placeholder="Product"
                              InputProps={{ sx: { fontSize: '0.75rem', minHeight: '28px' } }}
                            />
                          </TableCell>
                          <TableCell>
                            <DatePicker
                              value={orderChecklist?.door_orderdate ? parse(orderChecklist.door_orderdate, 'dd/MM/yyyy', new Date()) : null}
                              onChange={(newValue) => {
                                if (newValue) {
                                  const formattedDate = format(newValue, 'dd/MM/yyyy');
                                  setOrderChecklist((prev: any) => ({ ...prev, door_orderdate: formattedDate }));
                                  autoSaveChecklistField('door_orderdate', formattedDate);
                                }
                              }}
                          disabled={project?.status === 'remedial'}
                              format="dd/MM/yyyy"
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  fullWidth: false,
                                  variant: 'outlined',
                                  sx: { width: '115px' },
                                  InputProps: { sx: { fontSize: '0.70rem', height: '26px', padding: '2px' } }
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              fullWidth
                              value={orderChecklist?.door_orderby || ''}
                              onChange={(e) => {
                                setOrderChecklist((prev: any) => ({ ...prev, door_orderby: e.target.value }));
                                autoSaveChecklistField('door_orderby', e.target.value);
                              }}
                              variant="outlined"
                              displayEmpty
                              sx={{ fontSize: '0.75rem', height: '28px', '& .MuiSelect-select': { fontSize: '0.75rem', padding: '4px 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' } }}
                            >
                              <MenuItem value="">
                                <em style={{ fontSize: '0.75rem' }}>Select User</em>
                              </MenuItem>
                              {users.map((user: any) => (
                                <MenuItem key={user.id} value={user.id} sx={{ fontSize: '0.75rem' }}>
                                  {user.name || 'No Name'}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              fullWidth
                              value={orderChecklist?.door_checkedby || ''}
                              onChange={(e) => {
                                setOrderChecklist((prev: any) => ({ ...prev, door_checkedby: e.target.value }));
                                autoSaveChecklistField('door_checkedby', e.target.value);
                              }}
                              variant="outlined"
                              displayEmpty
                              sx={{ fontSize: '0.75rem', height: '28px', '& .MuiSelect-select': { fontSize: '0.75rem', padding: '4px 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' } }}
                            >
                              <MenuItem value="">
                                <em style={{ fontSize: '0.75rem' }}>Select User</em>
                              </MenuItem>
                              {users.map((user: any) => (
                                <MenuItem key={user.id} value={user.id} sx={{ fontSize: '0.75rem' }}>
                                  {user.name || 'No Name'}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell>
                            <DatePicker
                              value={orderChecklist?.door_Stock_due ? parse(orderChecklist.door_Stock_due, 'dd/MM/yyyy', new Date()) : null}
                              onChange={(newValue) => {
                                if (newValue) {
                                  const formattedDate = format(newValue, 'dd/MM/yyyy');
                                  setOrderChecklist((prev: any) => ({ ...prev, door_Stock_due: formattedDate }));
                                  autoSaveChecklistField('door_Stock_due', formattedDate);
                                }
                              }}
                          disabled={project?.status === 'remedial'}
                              format="dd/MM/yyyy"
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  fullWidth: false,
                                  variant: 'outlined',
                                  sx: { width: '115px' },
                                  InputProps: { sx: { fontSize: '0.70rem', height: '26px', padding: '2px' } }
                                }
                              }}
                            />
                          </TableCell>
                        </TableRow>

                        {/* Worktops Row */}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Worktops</TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              fullWidth
                              value={orderChecklist?.worktops_supplier || ''}
                              onChange={(e) => {
                                setOrderChecklist((prev: any) => ({ ...prev, worktops_supplier: e.target.value }));
                                autoSaveChecklistField('worktops_supplier', e.target.value);
                              }}
                              displayEmpty
                              sx={{ fontSize: '0.75rem', height: '28px' }}
                            >
                              <MenuItem value=""><em>Select Supplier</em></MenuItem>
                              {suppliers.map((supplier) => (
                                <MenuItem key={supplier.id} value={supplier.company}>
                                  {supplier.company}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            <TextField
                              disabled={project?.status === 'remedial'}
                              size="small"
                              fullWidth
                              multiline
                              minRows={1}
                              maxRows={3}
                              value={orderChecklist?.worktops || ''}
                              onChange={(e) => setOrderChecklist((prev: any) => ({ ...prev, worktops: e.target.value }))}
                              onBlur={(e) => autoSaveChecklistField('worktops', e.target.value)}
                              variant="outlined"
                              placeholder="Product"
                              InputProps={{ sx: { fontSize: '0.75rem', minHeight: '28px' } }}
                            />
                          </TableCell>
                          <TableCell>
                            <DatePicker
                              value={orderChecklist?.worktops_orderdate ? parse(orderChecklist.worktops_orderdate, 'dd/MM/yyyy', new Date()) : null}
                              onChange={(newValue) => {
                                if (newValue) {
                                  const formattedDate = format(newValue, 'dd/MM/yyyy');
                                  setOrderChecklist((prev: any) => ({ ...prev, worktops_orderdate: formattedDate }));
                                  autoSaveChecklistField('worktops_orderdate', formattedDate);
                                }
                              }}
                          disabled={project?.status === 'remedial'}
                              format="dd/MM/yyyy"
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  fullWidth: false,
                                  variant: 'outlined',
                                  sx: { width: '115px' },
                                  InputProps: { sx: { fontSize: '0.70rem', height: '26px', padding: '2px' } }
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              fullWidth
                              value={orderChecklist?.worktops_orderby || ''}
                              onChange={(e) => {
                                setOrderChecklist((prev: any) => ({ ...prev, worktops_orderby: e.target.value }));
                                autoSaveChecklistField('worktops_orderby', e.target.value);
                              }}
                              variant="outlined"
                              displayEmpty
                              sx={{ fontSize: '0.75rem', height: '28px', '& .MuiSelect-select': { fontSize: '0.75rem', padding: '4px 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' } }}
                            >
                              <MenuItem value="">
                                <em style={{ fontSize: '0.75rem' }}>Select User</em>
                              </MenuItem>
                              {users.map((user: any) => (
                                <MenuItem key={user.id} value={user.id} sx={{ fontSize: '0.75rem' }}>
                                  {user.name || 'No Name'}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              fullWidth
                              value={orderChecklist?.worktops_checkedby || ''}
                              onChange={(e) => {
                                setOrderChecklist((prev: any) => ({ ...prev, worktops_checkedby: e.target.value }));
                                autoSaveChecklistField('worktops_checkedby', e.target.value);
                              }}
                              variant="outlined"
                              displayEmpty
                              sx={{ fontSize: '0.75rem', height: '28px', '& .MuiSelect-select': { fontSize: '0.75rem', padding: '4px 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' } }}
                            >
                              <MenuItem value="">
                                <em style={{ fontSize: '0.75rem' }}>Select User</em>
                              </MenuItem>
                              {users.map((user: any) => (
                                <MenuItem key={user.id} value={user.id} sx={{ fontSize: '0.75rem' }}>
                                  {user.name || 'No Name'}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell>
                            <DatePicker
                              value={orderChecklist?.worktops_Stock_due ? parse(orderChecklist.worktops_Stock_due, 'dd/MM/yyyy', new Date()) : null}
                              onChange={(newValue) => {
                                if (newValue) {
                                  const formattedDate = format(newValue, 'dd/MM/yyyy');
                                  setOrderChecklist((prev: any) => ({ ...prev, worktops_Stock_due: formattedDate }));
                                  autoSaveChecklistField('worktops_Stock_due', formattedDate);
                                }
                              }}
                          disabled={project?.status === 'remedial'}
                              format="dd/MM/yyyy"
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  fullWidth: false,
                                  variant: 'outlined',
                                  sx: { width: '115px' },
                                  InputProps: { sx: { fontSize: '0.70rem', height: '26px', padding: '2px' } }
                                }
                              }}
                            />
                          </TableCell>
                        </TableRow>

                        {/* Sinks & Taps Row */}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Sinks & Taps</TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              fullWidth
                              value={orderChecklist?.sinks_taps_supplier || ''}
                              onChange={(e) => {
                                setOrderChecklist((prev: any) => ({ ...prev, sinks_taps_supplier: e.target.value }));
                                autoSaveChecklistField('sinks_taps_supplier', e.target.value);
                              }}
                              displayEmpty
                              sx={{ fontSize: '0.75rem', height: '28px' }}
                            >
                              <MenuItem value=""><em>Select Supplier</em></MenuItem>
                              {suppliers.map((supplier) => (
                                <MenuItem key={supplier.id} value={supplier.company}>
                                  {supplier.company}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            <TextField
                              disabled={project?.status === 'remedial'}
                              size="small"
                              fullWidth
                              value={orderChecklist?.sinks_taps || ''}
                              onChange={(e) => setOrderChecklist((prev: any) => ({ ...prev, sinks_taps: e.target.value }))}
                              onBlur={(e) => autoSaveChecklistField('sinks_taps', e.target.value)}
                              variant="outlined"
                              placeholder="Product"
                              multiline
                              minRows={1}
                              maxRows={3}
                              InputProps={{ sx: { fontSize: '0.75rem', minHeight: '28px' } }}
                            />
                          </TableCell>
                          <TableCell>
                            <DatePicker
                              value={orderChecklist?.sinks_taps_orderdate ? parse(orderChecklist.sinks_taps_orderdate, 'dd/MM/yyyy', new Date()) : null}
                              onChange={(newValue) => {
                                if (newValue) {
                                  const formattedDate = format(newValue, 'dd/MM/yyyy');
                                  setOrderChecklist((prev: any) => ({ ...prev, sinks_taps_orderdate: formattedDate }));
                                  autoSaveChecklistField('sinks_taps_orderdate', formattedDate);
                                }
                              }}
                          disabled={project?.status === 'remedial'}
                              format="dd/MM/yyyy"
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  fullWidth: false,
                                  variant: 'outlined',
                                  sx: { width: '115px' },
                                  InputProps: { sx: { fontSize: '0.70rem', height: '26px', padding: '2px' } }
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              fullWidth
                              value={orderChecklist?.sinks_taps_orderby || ''}
                              onChange={(e) => {
                                setOrderChecklist((prev: any) => ({ ...prev, sinks_taps_orderby: e.target.value }));
                                autoSaveChecklistField('sinks_taps_orderby', e.target.value);
                              }}
                              variant="outlined"
                              displayEmpty
                              sx={{ fontSize: '0.75rem', height: '28px', '& .MuiSelect-select': { fontSize: '0.75rem', padding: '4px 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' } }}
                            >
                              <MenuItem value="">
                                <em style={{ fontSize: '0.75rem' }}>Select User</em>
                              </MenuItem>
                              {users.map((user: any) => (
                                <MenuItem key={user.id} value={user.id} sx={{ fontSize: '0.75rem' }}>
                                  {user.name || 'No Name'}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              fullWidth
                              value={orderChecklist?.sinks_taps_checkedby || ''}
                              onChange={(e) => {
                                setOrderChecklist((prev: any) => ({ ...prev, sinks_taps_checkedby: e.target.value }));
                                autoSaveChecklistField('sinks_taps_checkedby', e.target.value);
                              }}
                              variant="outlined"
                              displayEmpty
                              sx={{ fontSize: '0.75rem', height: '28px', '& .MuiSelect-select': { fontSize: '0.75rem', padding: '4px 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' } }}
                            >
                              <MenuItem value="">
                                <em style={{ fontSize: '0.75rem' }}>Select User</em>
                              </MenuItem>
                              {users.map((user: any) => (
                                <MenuItem key={user.id} value={user.id} sx={{ fontSize: '0.75rem' }}>
                                  {user.name || 'No Name'}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell>
                            <DatePicker
                              value={orderChecklist?.sinks_taps_Stock_due ? parse(orderChecklist.sinks_taps_Stock_due, 'dd/MM/yyyy', new Date()) : null}
                              onChange={(newValue) => {
                                if (newValue) {
                                  const formattedDate = format(newValue, 'dd/MM/yyyy');
                                  setOrderChecklist((prev: any) => ({ ...prev, sinks_taps_Stock_due: formattedDate }));
                                  autoSaveChecklistField('sinks_taps_Stock_due', formattedDate);
                                }
                              }}
                          disabled={project?.status === 'remedial'}
                              format="dd/MM/yyyy"
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  fullWidth: false,
                                  variant: 'outlined',
                                  sx: { width: '115px' },
                                  InputProps: { sx: { fontSize: '0.70rem', height: '26px', padding: '2px' } }
                                }
                              }}
                            />
                          </TableCell>
                        </TableRow>

                        {/* PJH Appliances Row */}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>PJH Appliances</TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              fullWidth
                              value={orderChecklist?.pjh_appliances_supplier || ''}
                              onChange={(e) => {
                                setOrderChecklist((prev: any) => ({ ...prev, pjh_appliances_supplier: e.target.value }));
                                autoSaveChecklistField('pjh_appliances_supplier', e.target.value);
                              }}
                              displayEmpty
                              sx={{ fontSize: '0.75rem', height: '28px' }}
                            >
                              <MenuItem value=""><em>Select</em></MenuItem>
                              <MenuItem value="Yes">Yes</MenuItem>
                              <MenuItem value="No">No</MenuItem>
                            </Select>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            <TextField
                              disabled={project?.status === 'remedial'}
                              size="small"
                              fullWidth
                              value={orderChecklist?.pjh_appliances || ''}
                              onChange={(e) => setOrderChecklist((prev: any) => ({ ...prev, pjh_appliances: e.target.value }))}
                              onBlur={(e) => autoSaveChecklistField('pjh_appliances', e.target.value)}
                              variant="outlined"
                              placeholder="Product"
                              multiline
                              minRows={1}
                              maxRows={3}
                              InputProps={{ sx: { fontSize: '0.75rem', minHeight: '28px' } }}
                            />
                          </TableCell>
                          <TableCell>
                            <DatePicker
                              value={orderChecklist?.pjh_appliances_orderdate ? parse(orderChecklist.pjh_appliances_orderdate, 'dd/MM/yyyy', new Date()) : null}
                              onChange={(newValue) => {
                                if (newValue) {
                                  const formattedDate = format(newValue, 'dd/MM/yyyy');
                                  setOrderChecklist((prev: any) => ({ ...prev, pjh_appliances_orderdate: formattedDate }));
                                  autoSaveChecklistField('pjh_appliances_orderdate', formattedDate);
                                }
                              }}
                          disabled={project?.status === 'remedial'}
                              format="dd/MM/yyyy"
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  fullWidth: false,
                                  variant: 'outlined',
                                  sx: { width: '115px' },
                                  InputProps: { sx: { fontSize: '0.70rem', height: '26px', padding: '2px' } }
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              fullWidth
                              value={orderChecklist?.pjh_appliances_orderby || ''}
                              onChange={(e) => {
                                setOrderChecklist((prev: any) => ({ ...prev, pjh_appliances_orderby: e.target.value }));
                                autoSaveChecklistField('pjh_appliances_orderby', e.target.value);
                              }}
                              variant="outlined"
                              displayEmpty
                              sx={{ fontSize: '0.75rem', height: '28px', '& .MuiSelect-select': { fontSize: '0.75rem', padding: '4px 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' } }}
                            >
                              <MenuItem value="">
                                <em style={{ fontSize: '0.75rem' }}>Select User</em>
                              </MenuItem>
                              {users.map((user: any) => (
                                <MenuItem key={user.id} value={user.id} sx={{ fontSize: '0.75rem' }}>
                                  {user.name || 'No Name'}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              fullWidth
                              value={orderChecklist?.pjh_appliances_checkedby || ''}
                              onChange={(e) => {
                                setOrderChecklist((prev: any) => ({ ...prev, pjh_appliances_checkedby: e.target.value }));
                                autoSaveChecklistField('pjh_appliances_checkedby', e.target.value);
                              }}
                              variant="outlined"
                              displayEmpty
                              sx={{ fontSize: '0.75rem', height: '28px', '& .MuiSelect-select': { fontSize: '0.75rem', padding: '4px 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' } }}
                            >
                              <MenuItem value="">
                                <em style={{ fontSize: '0.75rem' }}>Select User</em>
                              </MenuItem>
                              {users.map((user: any) => (
                                <MenuItem key={user.id} value={user.id} sx={{ fontSize: '0.75rem' }}>
                                  {user.name || 'No Name'}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell>
                            <DatePicker
                              value={orderChecklist?.pjh_appliances_Stock_due ? parse(orderChecklist.pjh_appliances_Stock_due, 'dd/MM/yyyy', new Date()) : null}
                              onChange={(newValue) => {
                                if (newValue) {
                                  const formattedDate = format(newValue, 'dd/MM/yyyy');
                                  setOrderChecklist((prev: any) => ({ ...prev, pjh_appliances_Stock_due: formattedDate }));
                                  autoSaveChecklistField('pjh_appliances_Stock_due', formattedDate);
                                }
                              }}
                          disabled={project?.status === 'remedial'}
                              format="dd/MM/yyyy"
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  fullWidth: false,
                                  variant: 'outlined',
                                  sx: { width: '115px' },
                                  InputProps: { sx: { fontSize: '0.70rem', height: '26px', padding: '2px' } }
                                }
                              }}
                            />
                          </TableCell>
                        </TableRow>

                        {/* Dancia Handles etc Row */}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Dancia Handles etc</TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              fullWidth
                              value={orderChecklist?.dancia_handles_supplier || ''}
                              onChange={(e) => {
                                setOrderChecklist((prev: any) => ({ ...prev, dancia_handles_supplier: e.target.value }));
                                autoSaveChecklistField('dancia_handles_supplier', e.target.value);
                              }}
                              displayEmpty
                              sx={{ fontSize: '0.75rem', height: '28px' }}
                            >
                              <MenuItem value=""><em>Select</em></MenuItem>
                              <MenuItem value="Yes">Yes</MenuItem>
                              <MenuItem value="No">No</MenuItem>
                            </Select>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            <TextField
                              disabled={project?.status === 'remedial'}
                              size="small"
                              fullWidth
                              value={orderChecklist?.dancia_handles || ''}
                              onChange={(e) => setOrderChecklist((prev: any) => ({ ...prev, dancia_handles: e.target.value }))}
                              onBlur={(e) => autoSaveChecklistField('dancia_handles', e.target.value)}
                              variant="outlined"
                              placeholder="Product"
                              multiline
                              minRows={1}
                              maxRows={3}
                              InputProps={{ sx: { fontSize: '0.75rem', minHeight: '28px' } }}
                            />
                          </TableCell>
                          <TableCell>
                            <DatePicker
                              value={orderChecklist?.dancia_handles_orderdate ? parse(orderChecklist.dancia_handles_orderdate, 'dd/MM/yyyy', new Date()) : null}
                              onChange={(newValue) => {
                                if (newValue) {
                                  const formattedDate = format(newValue, 'dd/MM/yyyy');
                                  setOrderChecklist((prev: any) => ({ ...prev, dancia_handles_orderdate: formattedDate }));
                                  autoSaveChecklistField('dancia_handles_orderdate', formattedDate);
                                }
                              }}
                          disabled={project?.status === 'remedial'}
                              format="dd/MM/yyyy"
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  fullWidth: false,
                                  variant: 'outlined',
                                  sx: { width: '115px' },
                                  InputProps: { sx: { fontSize: '0.70rem', height: '26px', padding: '2px' } }
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              fullWidth
                              value={orderChecklist?.dancia_handles_orderby || ''}
                              onChange={(e) => {
                                setOrderChecklist((prev: any) => ({ ...prev, dancia_handles_orderby: e.target.value }));
                                autoSaveChecklistField('dancia_handles_orderby', e.target.value);
                              }}
                              variant="outlined"
                              displayEmpty
                              sx={{ fontSize: '0.75rem', height: '28px', '& .MuiSelect-select': { fontSize: '0.75rem', padding: '4px 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' } }}
                            >
                              <MenuItem value="">
                                <em style={{ fontSize: '0.75rem' }}>Select User</em>
                              </MenuItem>
                              {users.map((user: any) => (
                                <MenuItem key={user.id} value={user.id} sx={{ fontSize: '0.75rem' }}>
                                  {user.name || 'No Name'}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              fullWidth
                              value={orderChecklist?.dancia_handles_checkedby || ''}
                              onChange={(e) => {
                                setOrderChecklist((prev: any) => ({ ...prev, dancia_handles_checkedby: e.target.value }));
                                autoSaveChecklistField('dancia_handles_checkedby', e.target.value);
                              }}
                              variant="outlined"
                              displayEmpty
                              sx={{ fontSize: '0.75rem', height: '28px', '& .MuiSelect-select': { fontSize: '0.75rem', padding: '4px 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' } }}
                            >
                              <MenuItem value="">
                                <em style={{ fontSize: '0.75rem' }}>Select User</em>
                              </MenuItem>
                              {users.map((user: any) => (
                                <MenuItem key={user.id} value={user.id} sx={{ fontSize: '0.75rem' }}>
                                  {user.name || 'No Name'}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell>
                            <DatePicker
                              value={orderChecklist?.dancia_handles_Stock_due ? parse(orderChecklist.dancia_handles_Stock_due, 'dd/MM/yyyy', new Date()) : null}
                              onChange={(newValue) => {
                                if (newValue) {
                                  const formattedDate = format(newValue, 'dd/MM/yyyy');
                                  setOrderChecklist((prev: any) => ({ ...prev, dancia_handles_Stock_due: formattedDate }));
                                  autoSaveChecklistField('dancia_handles_Stock_due', formattedDate);
                                }
                              }}
                          disabled={project?.status === 'remedial'}
                              format="dd/MM/yyyy"
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  fullWidth: false,
                                  variant: 'outlined',
                                  sx: { width: '115px' },
                                  InputProps: { sx: { fontSize: '0.70rem', height: '26px', padding: '2px' } }
                                }
                              }}
                            />
                          </TableCell>
                        </TableRow>

                        {/* PBC Cabinets Row */}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>PBC Cabinets</TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              fullWidth
                              value={orderChecklist?.pbc_cabinets_supplier || ''}
                              onChange={(e) => {
                                setOrderChecklist((prev: any) => ({ ...prev, pbc_cabinets_supplier: e.target.value }));
                                autoSaveChecklistField('pbc_cabinets_supplier', e.target.value);
                              }}
                              displayEmpty
                              sx={{ fontSize: '0.75rem', height: '28px' }}
                            >
                              <MenuItem value=""><em>Select</em></MenuItem>
                              <MenuItem value="Yes">Yes</MenuItem>
                              <MenuItem value="No">No</MenuItem>
                            </Select>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            <TextField
                              disabled={project?.status === 'remedial'}
                              size="small"
                              fullWidth
                              value={orderChecklist?.pbc_cabinets || ''}
                              onChange={(e) => setOrderChecklist((prev: any) => ({ ...prev, pbc_cabinets: e.target.value }))}
                              onBlur={(e) => autoSaveChecklistField('pbc_cabinets', e.target.value)}
                              variant="outlined"
                              placeholder="Product"
                              multiline
                              minRows={1}
                              maxRows={3}
                              InputProps={{ sx: { fontSize: '0.75rem', minHeight: '28px' } }}
                            />
                          </TableCell>
                          <TableCell>
                            <DatePicker
                              value={orderChecklist?.pbc_cabinets_orderdate ? parse(orderChecklist.pbc_cabinets_orderdate, 'dd/MM/yyyy', new Date()) : null}
                              onChange={(newValue) => {
                                if (newValue) {
                                  const formattedDate = format(newValue, 'dd/MM/yyyy');
                                  setOrderChecklist((prev: any) => ({ ...prev, pbc_cabinets_orderdate: formattedDate }));
                                  autoSaveChecklistField('pbc_cabinets_orderdate', formattedDate);
                                }
                              }}
                          disabled={project?.status === 'remedial'}
                              format="dd/MM/yyyy"
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  fullWidth: false,
                                  variant: 'outlined',
                                  sx: { width: '115px' },
                                  InputProps: { sx: { fontSize: '0.70rem', height: '26px', padding: '2px' } }
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              fullWidth
                              value={orderChecklist?.pbc_cabinets_orderby || ''}
                              onChange={(e) => {
                                setOrderChecklist((prev: any) => ({ ...prev, pbc_cabinets_orderby: e.target.value }));
                                autoSaveChecklistField('pbc_cabinets_orderby', e.target.value);
                              }}
                              variant="outlined"
                              displayEmpty
                              sx={{ fontSize: '0.75rem', height: '28px', '& .MuiSelect-select': { fontSize: '0.75rem', padding: '4px 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' } }}
                            >
                              <MenuItem value="">
                                <em style={{ fontSize: '0.75rem' }}>Select User</em>
                              </MenuItem>
                              {users.map((user: any) => (
                                <MenuItem key={user.id} value={user.id} sx={{ fontSize: '0.75rem' }}>
                                  {user.name || 'No Name'}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              fullWidth
                              value={orderChecklist?.pbc_cabinets_checkedby || ''}
                              onChange={(e) => {
                                setOrderChecklist((prev: any) => ({ ...prev, pbc_cabinets_checkedby: e.target.value }));
                                autoSaveChecklistField('pbc_cabinets_checkedby', e.target.value);
                              }}
                              variant="outlined"
                              displayEmpty
                              sx={{ fontSize: '0.75rem', height: '28px', '& .MuiSelect-select': { fontSize: '0.75rem', padding: '4px 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' } }}
                            >
                              <MenuItem value="">
                                <em style={{ fontSize: '0.75rem' }}>Select User</em>
                              </MenuItem>
                              {users.map((user: any) => (
                                <MenuItem key={user.id} value={user.id} sx={{ fontSize: '0.75rem' }}>
                                  {user.name || 'No Name'}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell>
                            <DatePicker
                              value={orderChecklist?.pbc_cabinets_Stock_due ? parse(orderChecklist.pbc_cabinets_Stock_due, 'dd/MM/yyyy', new Date()) : null}
                              onChange={(newValue) => {
                                if (newValue) {
                                  const formattedDate = format(newValue, 'dd/MM/yyyy');
                                  setOrderChecklist((prev: any) => ({ ...prev, pbc_cabinets_Stock_due: formattedDate }));
                                  autoSaveChecklistField('pbc_cabinets_Stock_due', formattedDate);
                                }
                              }}
                          disabled={project?.status === 'remedial'}
                              format="dd/MM/yyyy"
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  fullWidth: false,
                                  variant: 'outlined',
                                  sx: { width: '115px' },
                                  InputProps: { sx: { fontSize: '0.70rem', height: '26px', padding: '2px' } }
                                }
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </Box>

            {/* Additional Services */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Additional Services
                </Typography>
                <Box display="flex" gap={2} flexDirection="column">
                  {/* Kitchen Fitter Row */}
                  <Box display="flex" gap={2} flexWrap="wrap">
                    <Box flex={1} minWidth={200}>
                      <FormControl fullWidth disabled={project?.status === 'remedial'}>
                        <InputLabel>Fitter Name</InputLabel>
                        <Select
                          value={project?.fitter_name || ''}
                          label="Fitter Name"
                          onChange={(e) => {
                            if (project) {
                              setProject({ ...project, fitter_name: e.target.value });
                              autoSaveField('fitter_name', e.target.value);
                            }
                          }}
                        >
                          <MenuItem value="">None</MenuItem>
                          {activeFitters.filter(f => f.specialty === 'Kitchen Fitter').map(f => (
                            <MenuItem key={f.id} value={f.name}>{f.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box flex={1} minWidth={150}>
                      <TextField
                        fullWidth
                        label="Fitter Cost"
                        type="number"
                        value={project?.fitter_balance || 0}
                        onChange={(e) => project && setProject({ ...project, fitter_balance: parseFloat(e.target.value) })}
                        onBlur={(e) => handleFieldBlur('fitter_balance', parseFloat(e.target.value))}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">£</InputAdornment>,
                        }}
                        disabled={project?.status === 'remedial'}
                      />
                    </Box>
                    <Box flex={1} minWidth={150}>
                      <TextField
                        fullWidth
                        label="Fit Start Date"
                        type="date"
                        value={project?.fitter_fit_start_date ? project.fitter_fit_start_date.split('T')[0] : ''}
                        onChange={(e) => {
                          if (project) {
                            const val = e.target.value || null;
                            setProject({ ...project, fitter_fit_start_date: val || undefined });
                            autoSaveField('fitter_fit_start_date', val);
                          }
                        }}
                        InputLabelProps={{ shrink: true }}
                        disabled={project?.status === 'remedial'}
                      />
                    </Box>
                    <Box flex={1} minWidth={150}>
                      <TextField
                        fullWidth
                        label="Fit End Date"
                        type="date"
                        value={project?.fitter_fit_end_date ? project.fitter_fit_end_date.split('T')[0] : ''}
                        onChange={(e) => {
                          if (project) {
                            const val = e.target.value || null;
                            setProject({ ...project, fitter_fit_end_date: val || undefined });
                            autoSaveField('fitter_fit_end_date', val);
                          }
                        }}
                        InputLabelProps={{ shrink: true }}
                        disabled={project?.status === 'remedial'}
                      />
                    </Box>
                  </Box>
                  <Box display="flex" gap={2} flexWrap="wrap">
                    <Box flex={1}>
                      <TextField
                        fullWidth
                        label="Fitter Details"
                        multiline
                        minRows={3}
                        value={project?.fitter_details || ''}
                        onChange={(e) => project && setProject({ ...project, fitter_details: e.target.value })}
                        onBlur={(e) => handleFieldBlur('fitter_details', e.target.value)}
                        placeholder="Details for the fitter diary entry"
                        disabled={project?.status === 'remedial'}
                        inputProps={{ style: { textTransform: 'uppercase' } }}
                      />
                    </Box>
                  </Box>
                  {/* Gas Fitter Row */}
                  <Box display="flex" gap={2} flexWrap="wrap">
                    <Box flex={1} minWidth={200}>
                      <FormControl fullWidth disabled={project?.status === 'remedial'}>
                        <InputLabel>Gas Fitter Name</InputLabel>
                        <Select
                          value={project?.gas_fitter_name || ''}
                          label="Gas Fitter Name"
                          onChange={(e) => {
                            if (project) {
                              setProject({ ...project, gas_fitter_name: e.target.value });
                              autoSaveField('gas_fitter_name', e.target.value);
                            }
                          }}
                        >
                          <MenuItem value="">None</MenuItem>
                          {activeFitters.filter(f => f.specialty === 'Gas Fitter').map(f => (
                            <MenuItem key={f.id} value={f.name}>{f.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box flex={1} minWidth={150}>
                      <TextField
                        fullWidth
                        label="Gas Fitter Cost"
                        type="number"
                        value={project?.gas_fitter_balance || 0}
                        onChange={(e) => project && setProject({ ...project, gas_fitter_balance: parseFloat(e.target.value) })}
                        onBlur={(e) => handleFieldBlur('gas_fitter_balance', parseFloat(e.target.value))}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">£</InputAdornment>,
                        }}
                        disabled={project?.status === 'remedial'}
                      />
                    </Box>
                    <Box flex={1} minWidth={150}>
                      <TextField
                        fullWidth
                        label="Gas Fit Start Date"
                        type="date"
                        value={project?.gas_fitter_fit_start_date ? project.gas_fitter_fit_start_date.split('T')[0] : ''}
                        onChange={(e) => {
                          if (project) {
                            const val = e.target.value || null;
                            setProject({ ...project, gas_fitter_fit_start_date: val || undefined });
                            autoSaveField('gas_fitter_fit_start_date', val);
                          }
                        }}
                        InputLabelProps={{ shrink: true }}
                        disabled={project?.status === 'remedial'}
                      />
                    </Box>
                    <Box flex={1} minWidth={150}>
                      <TextField
                        fullWidth
                        label="Gas Fit End Date"
                        type="date"
                        value={project?.gas_fitter_fit_end_date ? project.gas_fitter_fit_end_date.split('T')[0] : ''}
                        onChange={(e) => {
                          if (project) {
                            const val = e.target.value || null;
                            setProject({ ...project, gas_fitter_fit_end_date: val || undefined });
                            autoSaveField('gas_fitter_fit_end_date', val);
                          }
                        }}
                        InputLabelProps={{ shrink: true }}
                        disabled={project?.status === 'remedial'}
                      />
                    </Box>
                  </Box>
                  <Box display="flex" gap={2} flexWrap="wrap">
                    <Box flex={1}>
                      <TextField
                        fullWidth
                        label="Gas Fitter Details"
                        multiline
                        minRows={3}
                        value={project?.gas_fitter_details || ''}
                        onChange={(e) => project && setProject({ ...project, gas_fitter_details: e.target.value })}
                        onBlur={(e) => handleFieldBlur('gas_fitter_details', e.target.value)}
                        placeholder="Details for the gas fitter diary entry"
                        disabled={project?.status === 'remedial'}
                        inputProps={{ style: { textTransform: 'uppercase' } }}
                      />
                    </Box>
                  </Box>
                  {/* Additional Tradesmen (3 blocks) */}
                  {[1, 2, 3].map((n) => {
                    const suffix = n === 1 ? '' : String(n);
                    const tradeField = `tradesman${suffix}_trade`;
                    const balField = `tradesman${suffix}_balance`;
                    const startField = `tradesman${suffix}_fit_start_date`;
                    const endField = `tradesman${suffix}_fit_end_date`;
                    const detailsField = `tradesman${suffix}_details`;
                    const p = project as any;
                    return (
                      <Box key={n} display="flex" flexDirection="column" gap={2}>
                        <Box display="flex" gap={2} flexWrap="wrap">
                          <Box flex={1} minWidth={200}>
                            <FormControl fullWidth disabled={project?.status === 'remedial'}>
                              <InputLabel>{`Additional Tradesman ${n}`}</InputLabel>
                              <Select
                                value={p?.[tradeField] || ''}
                                label={`Additional Tradesman ${n}`}
                                onChange={(e) => {
                                  if (project) {
                                    setProject({ ...project, [tradeField]: e.target.value } as Project);
                                    autoSaveField(tradeField, e.target.value);
                                  }
                                }}
                              >
                                <MenuItem value="">None</MenuItem>
                                <MenuItem value="Electrician">Electrician</MenuItem>
                                <MenuItem value="Plastering">Plastering</MenuItem>
                                <MenuItem value="Tiling">Tiling</MenuItem>
                                <MenuItem value="Plumbing">Plumbing</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                          <Box flex={1} minWidth={150}>
                            <TextField
                              fullWidth
                              label="Tradesman Cost"
                              type="number"
                              value={p?.[balField] || 0}
                              onChange={(e) => project && setProject({ ...project, [balField]: parseFloat(e.target.value) } as Project)}
                              onBlur={(e) => handleFieldBlur(balField, parseFloat(e.target.value))}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">£</InputAdornment>,
                              }}
                              disabled={project?.status === 'remedial'}
                            />
                          </Box>
                          <Box flex={1} minWidth={150}>
                            <TextField
                              fullWidth
                              label="Start Date"
                              type="date"
                              value={p?.[startField] ? String(p[startField]).split('T')[0] : ''}
                              onChange={(e) => {
                                if (project) {
                                  const val = e.target.value || null;
                                  setProject({ ...project, [startField]: val || undefined } as Project);
                                  autoSaveField(startField, val);
                                }
                              }}
                              InputLabelProps={{ shrink: true }}
                              disabled={project?.status === 'remedial'}
                            />
                          </Box>
                          <Box flex={1} minWidth={150}>
                            <TextField
                              fullWidth
                              label="End Date"
                              type="date"
                              value={p?.[endField] ? String(p[endField]).split('T')[0] : ''}
                              onChange={(e) => {
                                if (project) {
                                  const val = e.target.value || null;
                                  setProject({ ...project, [endField]: val || undefined } as Project);
                                  autoSaveField(endField, val);
                                }
                              }}
                              InputLabelProps={{ shrink: true }}
                              disabled={project?.status === 'remedial'}
                            />
                          </Box>
                        </Box>
                        <Box display="flex" gap={2} flexWrap="wrap">
                          <Box flex={1}>
                            <TextField
                              fullWidth
                              label="Tradesman Details"
                              multiline
                              minRows={3}
                              value={p?.[detailsField] || ''}
                              onChange={(e) => project && setProject({ ...project, [detailsField]: e.target.value } as Project)}
                              onBlur={(e) => handleFieldBlur(detailsField, e.target.value)}
                              placeholder={`Details for additional tradesman ${n}`}
                              disabled={project?.status === 'remedial'}
                              inputProps={{ style: { textTransform: 'uppercase' } }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>

            {/* Pictures Section */}
            {(photos.length > 0 || (isTablet && !photosLoaded)) && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhotoCamera color="primary" />
                      Pictures
                    </Typography>
                    {selectedPhotoIds.size > 0 && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setSelectedPhotoIds(new Set())}
                        >
                          Clear
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Send />}
                          onClick={() => {
                            const selectedPhotos = photos.filter(p => selectedPhotoIds.has(p.id)).map(p => ({
                              ...p,
                              source_table: p.source_table || 'project_photos'
                            }));
                            setSelectedDocsForEmail(selectedPhotos);
                            setEmailModalOpen(true);
                          }}
                        >
                          Email Selected ({selectedPhotoIds.size})
                        </Button>
                      </Box>
                    )}
                  </Box>
                  {/* On tablets, show load button if not yet loaded */}
                  {isTablet && !photosLoaded ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <Button
                        variant="outlined"
                        startIcon={<PhotoCamera />}
                        onClick={() => {
                          setPhotosLoaded(true);
                          fetchPhotos();
                        }}
                      >
                        Load Pictures
                      </Button>
                    </Box>
                  ) : photos.length === 0 ? (
                    <Typography color="text.secondary">No pictures uploaded</Typography>
                  ) : null}
                  {(!isTablet || photosLoaded) && photos.length > 0 && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                    {photos.filter((photo: PhotoData) => photo.photo_type !== 'document').map((photo: PhotoData) => (
                      <Box
                        key={photo.id}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          '&:hover': { boxShadow: 2 }
                        }}
                      >
                        <Box
                          sx={{
                            height: 200,
                            bgcolor: 'grey.100',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative'
                          }}
                          onClick={() => {
                            // Open image/PDF in modal with rotation
                            setSelectedDocument({
                              ...photo,
                              viewUrl: photo.fileUrl || `/uploads/${photo.file_path}`,
                              mime_type: photo.mime_type || 'image/jpeg',
                              rotation: photoRotations[photo.id] || 0
                            });
                          }}
                        >
                          {photo.mime_type === 'application/pdf' ? (
                            <Box sx={{ textAlign: 'center' }}>
                              <PictureAsPdf sx={{ fontSize: 48, color: '#d32f2f', mb: 1 }} />
                              <Typography variant="caption" color="textSecondary">
                                PDF Document
                              </Typography>
                            </Box>
                          ) : (
                            <img
                              src={photo.fileUrl || (photo.source_table === 'documents'
                                ? `/api/documents/view/${photo.id}`
                                : `/api/project-photos/uploads/${photo.file_path}`)}
                              alt={photo.filename}
                              style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                transform: `rotate(${photoRotations[photo.id] || 0}deg)`,
                                transition: 'transform 0.3s ease'
                              }}
                              onError={(e) => {
                                const img = e.target as HTMLImageElement;
                                if (!img.dataset.failed) {
                                  img.dataset.failed = 'true';
                                  console.error('Image failed to load:', photo.id, photo.filename);
                                  img.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';
                                }
                              }}
                            />
                          )}
                          {/* Rotate buttons */}
                          <IconButton
                            sx={{
                              position: 'absolute',
                              bottom: 8,
                              left: 8,
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }
                            }}
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRotatePhoto(photo.id, 'left', photo.source_table);
                            }}
                          >
                            <RotateLeft fontSize="small" />
                          </IconButton>
                          <IconButton
                            sx={{
                              position: 'absolute',
                              bottom: 8,
                              left: 48,
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }
                            }}
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRotatePhoto(photo.id, 'right', photo.source_table);
                            }}
                          >
                            <RotateRight fontSize="small" />
                          </IconButton>
                          {/* Selection checkbox */}
                          <Checkbox
                            sx={{
                              position: 'absolute',
                              top: 4,
                              left: 4,
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                              borderRadius: 1,
                              padding: '2px',
                              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }
                            }}
                            size="small"
                            checked={selectedPhotoIds.has(photo.id)}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPhotoIds(prev => {
                                const newSet = new Set(prev);
                                if (newSet.has(photo.id)) {
                                  newSet.delete(photo.id);
                                } else {
                                  newSet.add(photo.id);
                                }
                                return newSet;
                              });
                            }}
                          />
                          {/* Delete button */}
                          <IconButton
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }
                            }}
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePhoto(photo.id, photo.source_table);
                            }}
                          >
                            <Delete fontSize="small" color="error" />
                          </IconButton>
                          {/* Email button */}
                          <IconButton
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 48,
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }
                            }}
                            size="small"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedDocForEmail({
                                id: photo.id,
                                filename: photo.filename,
                                file_path: photo.file_path,
                                photo_type: photo.photo_type
                              });
                              setEmailModalOpen(true);
                            }}
                          >
                            <Send fontSize="small" />
                          </IconButton>
                          {/* Rename button */}
                          <IconButton
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 88,
                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }
                              }}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setRenamePhotoDialog({
                                  open: true,
                                  photo: photo,
                                  newName: photo.description || photo.filename || ''
                                });
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                        </Box>
                        <Box sx={{ p: 1 }}>
                          <Typography variant="body2" noWrap title={photo.description && !photo.description.match(/^Uploaded \d+ photo/) ? photo.description : photo.filename}>
                            {photo.description && !photo.description.match(/^Uploaded \d+ photo/) ? photo.description : photo.filename}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Type: {photo.photo_type}
                          </Typography>
                          {photo.fitter_sent_at && (
                            <Typography variant="caption" color="success.main" display="block" sx={{ fontWeight: 'bold' }}>
                              Sent to Fitters: {new Date(photo.fitter_sent_at).toLocaleDateString('en-GB')}
                            </Typography>
                          )}
                          {photo.customer_sent_at && (
                            <Typography variant="caption" color="success.main" display="block" sx={{ fontWeight: 'bold' }}>
                              Sent to {photo.customer_sent_to || 'customer'} on {new Date(photo.customer_sent_at).toLocaleDateString('en-GB')}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Documents Section - Only shows documents from project_documents and documents tables */}
            {(documents.length > 0 || (isTablet && !documentsLoaded)) && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Description color="primary" />
                      Documents
                    </Typography>
                    {selectedDocIds.size > 0 && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setSelectedDocIds(new Set())}
                        >
                          Clear
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<Delete />}
                          onClick={handleDeleteSelectedDocuments}
                        >
                          Delete ({selectedDocIds.size})
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Send />}
                          onClick={() => {
                            const selectedDocs = documents.filter((d: any) => selectedDocIds.has(d.id)).map((d: any) => ({
                              ...d,
                              source_table: d.source_table || d.source_type || 'documents'
                            }));

                            // Check if all selected docs are supplier orders for the same supplier
                            const supplierNames = selectedDocs.map((d: any) => {
                              const docName = d.original_name || d.description || d.document_name || d.filename || '';
                              const match = docName.match(/Supplier Order - (.+)\.docx/);
                              if (!match) return null;
                              return match[1].replace(/\s*\([^)]+\)\s*$/, '').trim();
                            });
                            const allSupplierOrders = supplierNames.every((name: string | null) => name !== null);
                            const uniqueSuppliers = new Set(supplierNames.filter(Boolean));

                            if (allSupplierOrders && uniqueSuppliers.size === 1) {
                              // All selected docs are supplier orders for the same supplier
                              handleOpenSupplierSendModal(selectedDocs);
                            } else {
                              // Show a tip about supplier order grouping
                              if (allSupplierOrders && uniqueSuppliers.size > 1) {
                                setSnackbarMessage('Tip: To send directly to a supplier, select orders for the same supplier only.');
                                setSnackbarOpen(true);
                              } else if (supplierNames.some((name: string | null) => name !== null)) {
                                setSnackbarMessage('Tip: To send supplier orders directly, select only supplier order documents for the same supplier.');
                                setSnackbarOpen(true);
                              }
                              setSelectedDocsForEmail(selectedDocs);
                              setEmailModalOpen(true);
                            }
                          }}
                        >
                          Email Selected ({selectedDocIds.size})
                        </Button>
                      </Box>
                    )}
                  </Box>
                  {/* On tablets, show load button if not yet loaded */}
                  {isTablet && !documentsLoaded ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <Button
                        variant="outlined"
                        startIcon={<Description />}
                        onClick={() => {
                          setDocumentsLoaded(true);
                          fetchDocuments();
                          fetchInvoices();
                        }}
                      >
                        Load Documents
                      </Button>
                    </Box>
                  ) : documents.length === 0 ? (
                    <Typography color="text.secondary">No documents uploaded</Typography>
                  ) : null}
                  {(!isTablet || documentsLoaded) && documents.length > 0 && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                    {/* Show documents from project_documents and documents tables */}
                    {documents.map((doc: any) => (
                      <Box
                        key={`doc-${doc.id}`}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          overflow: 'hidden',
                          position: 'relative',
                          // On touch devices, always show buttons; on desktop, show on hover
                          '& .delete-btn, & .email-btn': {
                            opacity: isMobileOrTablet ? 1 : 0
                          },
                          '&:hover': {
                            boxShadow: 2,
                            '& .delete-btn': {
                              opacity: 1
                            },
                            '& .email-btn': {
                              opacity: 1
                            }
                          }
                        }}
                      >
                        {/* Selection checkbox */}
                        <Checkbox
                          sx={{
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            zIndex: 1,
                            bgcolor: 'background.paper',
                            borderRadius: 1,
                            padding: '2px',
                          }}
                          size="small"
                          checked={selectedDocIds.has(doc.id)}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDocIds(prev => {
                              const newSet = new Set(prev);
                              if (newSet.has(doc.id)) {
                                newSet.delete(doc.id);
                              } else {
                                newSet.add(doc.id);
                              }
                              return newSet;
                            });
                          }}
                        />

                        {/* Delete button */}
                        <IconButton
                          className="delete-btn"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteDocument(doc.id, doc.source_type);
                          }}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            zIndex: 1,
                            bgcolor: 'background.paper',
                            transition: 'opacity 0.2s',
                            '&:hover': {
                              bgcolor: 'error.light',
                              color: 'white'
                            }
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>

                        {/* Email button for all documents */}
                        <IconButton
                          className="email-btn"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDocForEmail(doc);
                            setEmailModalOpen(true);
                          }}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 44,
                            zIndex: 1,
                            bgcolor: 'background.paper',
                            transition: 'opacity 0.2s',
                            '&:hover': {
                              bgcolor: 'primary.light',
                                color: 'white'
                              }
                            }}
                          >
                            <Send fontSize="small" />
                          </IconButton>

                        <Box
                          sx={{
                            height: 200,
                            bgcolor: 'grey.100',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            cursor: 'pointer'
                          }}
                          onClick={() => {
                            // For Word documents and supplier orders, open in modal with download button
                            if (doc.filename?.toLowerCase().endsWith('.docx') || 
                                doc.filename?.toLowerCase().endsWith('.doc') ||
                                doc.document_name?.toLowerCase().endsWith('.docx') ||
                                doc.document_name?.toLowerCase().endsWith('.doc') ||
                                doc.file_path?.toLowerCase().endsWith('.docx') ||
                                doc.file_path?.toLowerCase().endsWith('.doc') ||
                                doc.document_type === 'letter' ||
                                doc.document_type === 'supplier_order' ||
                                (doc.document_name && doc.document_name.includes('Supplier Order')) ||
                                (doc.original_name && doc.original_name.includes('Customer Invoice'))) {
                              // Set up the document for modal viewing with download option
                              let downloadUrl;
                              
                              // For documents from the documents table, use the download API endpoint
                              if (doc.source_table === 'documents') {
                                downloadUrl = `/api/documents/download/${doc.id}`;
                              } else if (doc.file_path?.startsWith('/documents/')) {
                                downloadUrl = doc.file_path;
                              } else if (doc.file_path?.startsWith('/uploads/')) {
                                // File path already includes /uploads/ prefix
                                downloadUrl = doc.file_path;
                              } else {
                                // File path needs /uploads/ prefix
                                downloadUrl = `/uploads/${doc.file_path}`;
                              }
                              
                              setSelectedDocument({
                                ...doc,
                                viewUrl: downloadUrl,
                                mime_type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                                isWordDoc: true
                              });
                              return;
                            }
                            
                            // Set proper viewUrl based on document source for other files
                            let viewUrl;
                            if (doc.source_table === 'documents') {
                              viewUrl = `/api/documents/view/${doc.id}`;
                            } else if (doc.source_table === 'project_documents') {
                              // For project_documents, use the file_path directly
                              viewUrl = doc.file_path;
                            } else {
                              // Fallback
                              viewUrl = `/uploads/${doc.file_path}`;
                            }
                            
                            setSelectedDocument({
                              ...doc,
                              viewUrl: viewUrl,
                              mime_type: doc.mime_type || (doc.filename?.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg'),
                              rotation: documentRotations[doc.id] || 0
                            });
                          }}
                        >
                          {/* Check if document is an image and show preview */}
                          {(doc.mime_type?.startsWith('image/') ||
                            doc.filename?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i) ||
                            doc.file_path?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i)) ? (
                            <>
                              <img
                                src={doc.source_table === 'documents'
                                  ? `/api/documents/view/${doc.id}`
                                  : `/uploads/${doc.file_path}`}
                                alt={doc.filename || doc.document_name}
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '100%',
                                  objectFit: 'contain',
                                  transform: `rotate(${documentRotations[doc.id] || 0}deg)`,
                                  transition: 'transform 0.3s ease'
                                }}
                                onError={(e) => {
                                  // Fallback to icon if image fails to load
                                  console.error('Image preview failed:', doc.file_path);
                                  const img = e.target as HTMLImageElement;
                                  img.style.display = 'none';
                                }}
                              />
                              {/* Rotate buttons for image documents */}
                              <IconButton
                                sx={{
                                  position: 'absolute',
                                  bottom: 8,
                                  left: 8,
                                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }
                                }}
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRotateDocument(doc.id, 'left');
                                }}
                              >
                                <RotateLeft fontSize="small" />
                              </IconButton>
                              <IconButton
                                sx={{
                                  position: 'absolute',
                                  bottom: 8,
                                  left: 48,
                                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }
                                }}
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRotateDocument(doc.id, 'right');
                                }}
                              >
                                <RotateRight fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <Box sx={{ textAlign: 'center' }}>
                              {doc.filename?.toLowerCase().endsWith('.pdf') ? (
                                <>
                                  <PictureAsPdf sx={{ fontSize: 48, color: '#d32f2f', mb: 1 }} />
                                  <Typography variant="caption" color="textSecondary">
                                    PDF Document
                                  </Typography>
                                </>
                              ) : doc.filename?.toLowerCase().match(/\.(doc|docx)$/) || doc.document_type === 'letter' || doc.document_type === 'supplier_order' || doc.document_type === 'delivery_note' ? (
                                <>
                                  <Description sx={{ fontSize: 48, color: '#1976d2', mb: 1 }} />
                                  <Typography variant="caption" color="textSecondary">
                                    {doc.document_type === 'supplier_order' ? 'DOCX' : 'Word Document'}
                                  </Typography>
                                </>
                              ) : (
                                <>
                                  <Description sx={{ fontSize: 48, color: '#757575', mb: 1 }} />
                                  <Typography variant="caption" color="textSecondary">
                                    {doc.document_type || 'Document'}
                                  </Typography>
                                </>
                              )}
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ p: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" noWrap title={doc.description || doc.document_name || doc.filename}>
                                {doc.description || doc.document_name || doc.filename}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" display="block">
                                {doc.document_type === 'delivery_note' ? 'Delivery Note' :
                                 doc.document_type === 'letter' ? 'Installation Letter' :
                                 doc.document_type === 'supplier_order' ? 'DOCX' :
                                 doc.document_type === 'quote' ? 'Quotation' :
                                 doc.document_type === 'invoice' ? 'Invoice' :
                                 'Document'}
                              </Typography>
                              {doc.document_number && (
                                <Typography variant="caption" color="primary" display="block">
                                  {doc.document_number}
                                </Typography>
                              )}
                              <Typography variant="caption" color="textSecondary" display="block">
                                {doc.uploaded_at ? formatDateForDisplay(doc.uploaded_at) : doc.created_at ? formatDateForDisplay(doc.created_at) : ''}
                              </Typography>
                              {doc.fitter_sent_at && (
                                <Typography variant="caption" color="success.main" display="block" sx={{ fontWeight: 'bold' }}>
                                  Sent to Fitters: {new Date(doc.fitter_sent_at).toLocaleDateString('en-GB')}
                                </Typography>
                              )}
                              {doc.customer_sent_at && (
                                <Typography variant="caption" color="success.main" display="block" sx={{ fontWeight: 'bold' }}>
                                  Sent to {doc.customer_sent_to || 'customer'} on {new Date(doc.customer_sent_at).toLocaleDateString('en-GB')}
                                </Typography>
                              )}
                            </Box>
                            {/* Send to Supplier button for Supplier Order documents */}
                            {(doc.document_name?.includes('Supplier Order') || doc.document_type === 'supplier_order' || doc.description?.includes('Supplier Order')) && (
                              <Tooltip title={
                                doc.supplier_sent_at || supplierSendSuccess?.docId === doc.id
                                  ? `Sent ${doc.supplier_sent_at ? new Date(doc.supplier_sent_at).toLocaleDateString('en-GB') : 'just now'}`
                                  : "Send to Supplier"
                              }>
                                <span>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color={(doc.supplier_sent_at || supplierSendSuccess?.docId === doc.id) ? "success" : "primary"}
                                    disabled={sendingToSupplier === doc.id}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenSupplierSendModal(doc);
                                    }}
                                    startIcon={sendingToSupplier === doc.id ? <CircularProgress size={14} color="inherit" /> :
                                              (doc.supplier_sent_at || supplierSendSuccess?.docId === doc.id) ? <CheckCircle /> : <LocalShipping />}
                                    sx={{ minWidth: 'auto', fontSize: '0.6rem', py: 0.3, px: 0.8, ml: 1, whiteSpace: 'nowrap' }}
                                  >
                                    {sendingToSupplier === doc.id ? 'Sending...' :
                                     (doc.supplier_sent_at || supplierSendSuccess?.docId === doc.id)
                                       ? `Sent to Supplier on ${doc.supplier_sent_at ? new Date(doc.supplier_sent_at).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB')}`
                                       : 'Send to Supplier'}
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Invoices Section */}
            {invoices.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Article color="primary" />
                    Invoices
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Invoice #</strong></TableCell>
                          <TableCell><strong>Amount</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell><strong>Due Date</strong></TableCell>
                          <TableCell><strong>Created</strong></TableCell>
                          <TableCell><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invoices.map((invoice: any) => {
                          const isOverdue = invoice.status !== 'paid' && invoice.due_date && new Date(invoice.due_date) < new Date();

                          return (
                            <TableRow
                              key={invoice.id}
                              sx={{
                                bgcolor: isOverdue ? 'error.light' : 'inherit',
                                '&:hover': {
                                  bgcolor: isOverdue ? 'error.light' : 'action.hover'
                                }
                              }}
                            >
                              <TableCell>{invoice.invoice_number}</TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">
                                  £{parseFloat(invoice.total_amount).toFixed(2)}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  Net: £{parseFloat(invoice.amount).toFixed(2)} + VAT: £{parseFloat(invoice.tax_amount).toFixed(2)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box
                                  sx={{
                                    display: 'inline-block',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontSize: '0.75rem',
                                    fontWeight: 'bold',
                                    textTransform: 'uppercase',
                                    bgcolor: invoice.status === 'paid' ? 'success.main' :
                                            invoice.status === 'cancelled' ? 'grey.500' :
                                            (isOverdue && invoice.status !== 'paid' && invoice.status !== 'cancelled') ? 'error.main' :
                                            'primary.main',
                                    color: 'white'
                                  }}
                                >
                                  {invoice.status === 'cancelled' ? 'Cancelled' :
                                   (isOverdue && invoice.status !== 'paid' && invoice.status !== 'cancelled') ? 'Overdue' :
                                   invoice.status === 'sent' ? 'Sent' : invoice.status}
                                </Box>
                              </TableCell>
                              <TableCell>
                                {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString('en-GB') : '-'}
                              </TableCell>
                              <TableCell>
                                {invoice.created_at ? new Date(invoice.created_at).toLocaleDateString('en-GB') : '-'}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<GetApp />}
                                    onClick={async () => {
                                      try {
                                        // Find the document associated with this invoice
                                        const token = localStorage.getItem('token');
                                        const docsResponse = await axios.get(`${API_BASE_URL}/documents`, {
                                          headers: { 'Authorization': `Bearer ${token}` }
                                        });
                                        const invoiceDoc = docsResponse.data.documents?.find(
                                          (doc: any) => doc.related_to === 'Invoice' && doc.related_id === invoice.id
                                        );

                                        if (invoiceDoc) {
                                          window.open(`/api/documents/download/${invoiceDoc.id}`, '_blank');
                                        } else {
                                          alert('Invoice document not found');
                                        }
                                      } catch (error) {
                                        console.error('Error downloading invoice:', error);
                                        alert('Failed to download invoice');
                                      }
                                    }}
                                  >
                                    Download
                                  </Button>
                                  {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                                    <>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        color="success"
                                        onClick={async () => {
                                          if (window.confirm('Mark this invoice as paid?')) {
                                            try {
                                              const token = localStorage.getItem('token');
                                              await axios.put(
                                                `/api/invoices/${invoice.id}`,
                                                {
                                                  ...invoice,
                                                  status: 'paid',
                                                  paid_date: new Date().toISOString().split('T')[0]
                                                },
                                                {
                                                  headers: { 'Authorization': `Bearer ${token}` }
                                                }
                                              );

                                              setSnackbarMessage('Invoice marked as paid');
                                              setSnackbarOpen(true);
                                              await fetchInvoices();
                                            } catch (error) {
                                              console.error('Error marking invoice as paid:', error);
                                              alert('Failed to mark invoice as paid');
                                            }
                                          }
                                        }}
                                      >
                                        Mark Paid
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        onClick={async () => {
                                          if (window.confirm('Cancel this invoice?')) {
                                            try {
                                              const token = localStorage.getItem('token');
                                              await axios.put(
                                                `/api/invoices/${invoice.id}`,
                                                {
                                                  ...invoice,
                                                  status: 'cancelled'
                                                },
                                                {
                                                  headers: { 'Authorization': `Bearer ${token}` }
                                                }
                                              );

                                              setSnackbarMessage('Invoice cancelled');
                                              setSnackbarOpen(true);
                                              await fetchInvoices();
                                            } catch (error) {
                                              console.error('Error cancelling invoice:', error);
                                              alert('Failed to cancel invoice');
                                            }
                                          }
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                    </>
                                  )}
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={async () => {
                                      if (window.confirm('Are you sure you want to delete this invoice? This will also delete the associated document.')) {
                                        try {
                                          const token = localStorage.getItem('token');

                                          // Delete the invoice
                                          await axios.delete(`/api/invoices/${invoice.id}`, {
                                            headers: { 'Authorization': `Bearer ${token}` }
                                          });

                                          setSnackbarMessage('Invoice deleted successfully');
                                          setSnackbarOpen(true);
                                          await fetchInvoices();
                                          await fetchDocuments();
                                        } catch (error) {
                                          console.error('Error deleting invoice:', error);
                                          alert('Failed to delete invoice');
                                        }
                                      }
                                    }}
                                  >
                                    <Delete />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Financial Information - Moved to main area */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachMoney color="primary" />
                    Financial Information
                  </Typography>
                  <Button
                    variant="contained"
                    color="success"
                    size="medium"
                    onClick={handleCreateCustomerInvoice}
                    disabled={creatingInvoice || orderItems.length === 0 || project?.status === 'remedial' || project?.status === 'remedial_resolved'}
                  >
                    {creatingInvoice ? 'Creating Invoice...' : 'Create Customer Invoice'}
                  </Button>
                </Box>
                
                {/* Cost Breakdown Section */}
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, mb: 1 }}>
                  Cost Breakdown
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    label="Total Material Cost"
                    type="number"
                    size="small"
                    value={project?.material_cost || 0}
                    onChange={(e) => project && setProject({ ...project, material_cost: parseFloat(e.target.value) || 0 })}
                    onBlur={(e) => handleFieldBlur('material_cost', parseFloat(e.target.value) || 0)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">£</InputAdornment>,
                    }}
                    sx={{ maxWidth: 300 }}
                    disabled={project?.status === 'remedial'}
                  />
                </Box>
                
                {/* Summary Section */}
                <Divider sx={{ my: 2 }} />
                <Box display="flex" gap={3} flexWrap="wrap">
                  <Box flex={1} minWidth={250}>
                    <Box sx={{ bgcolor: 'info.main', color: 'white', p: 2, borderRadius: 1, height: '80px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography variant="body2">Total Project Cost</Typography>
                      <Typography variant="h5">£{formatCurrency(calculateProjectCost())}</Typography>
                    </Box>
                  </Box>
                  
                  <Box flex={1} minWidth={250}>
                    <TextField
                      fullWidth
                      label="Deposit Taken"
                      type="number"
                      value={project?.deposit_amount || 0}
                      onChange={(e) => project && setProject({ ...project, deposit_amount: parseFloat(e.target.value) })}
                      onBlur={(e) => handleFieldBlur('deposit_amount', parseFloat(e.target.value))}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">£</InputAdornment>,
                      }}
                      sx={{
                        '& .MuiInputBase-root': {
                          height: '80px',
                          '& input': {
                            fontSize: '1.4rem'
                          }
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '1rem'
                        }
                      }}
                      disabled={project?.status === 'remedial'}
                    />
                  </Box>
                  
                  <Box flex={1} minWidth={250}>
                    <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 2, borderRadius: 1, height: '80px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography variant="body2">Balance Remaining</Typography>
                      <Typography variant="h5">£{formatCurrency(calculateBalance())}</Typography>
                    </Box>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  * These totals do not include fitting and gas fitting costs
                </Typography>

                <Divider sx={{ my: 3 }} />
                
                <Box display="flex" gap={3} flexWrap="wrap">
                  <Box flex={1} minWidth={300}>
                    <Typography variant="subtitle2" gutterBottom>
                      Payment History
                    </Typography>
                    
                    <List dense>
                      {/* Show deposit as first item in payment history */}
                      {project?.deposit_amount && project.deposit_amount > 0 && (
                        <ListItem
                          sx={{
                            bgcolor: '#f0f7ff',
                            borderRadius: 1,
                            mb: 1,
                            border: '1px solid #90caf9'
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2">
                                  £{formatCurrency(project.deposit_amount)} - Initial Deposit
                                </Typography>
                                <Chip
                                  label="Deposit"
                                  size="small"
                                  color="primary"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              </Box>
                            }
                            secondary="Recorded in Deposit Taken field above"
                          />
                        </ListItem>
                      )}

                      {/* Additional payments */}
                      {payments.map((payment) => (
                        <ListItem
                          key={payment.id}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => payment.id && handleDeletePayment(payment.id)}
                              disabled={project?.status === 'remedial'}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          }
                        >
                          <ListItemText
                            primary={`£${formatCurrency(payment.amount)} - ${payment.description}`}
                            secondary={formatDateForDisplay(payment.payment_date)}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                  
                  <Box flex={1} minWidth={300}>
                    <Typography variant="subtitle2" gutterBottom>
                      Add Payment
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={1}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Amount"
                        type="number"
                        value={newPayment.amount}
                        onChange={(e) => setNewPayment({ ...newPayment, amount: parseFloat(e.target.value) })}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">£</InputAdornment>,
                        }}
                        disabled={project?.status === 'remedial'}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Description"
                        value={newPayment.description}
                        onChange={(e) => setNewPayment({ ...newPayment, description: e.target.value })}
                        disabled={project?.status === 'remedial'}
                      />
                      <DatePicker
                        label="Payment Date"
                        value={new Date(newPayment.payment_date)}
                        onChange={(date) => date && setNewPayment({ ...newPayment, payment_date: format(date, 'yyyy-MM-dd') })}
                        format="dd/MM/yyyy"
                        disabled={project?.status === 'remedial'}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                          },
                        }}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Add />}
                        onClick={handleAddPayment}
                        disabled={!newPayment.amount || !newPayment.description || project?.status === 'remedial' || project?.status === 'remedial_resolved'}
                      >
                        Add Payment
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
        </Box>

        {/* Picture Upload Dialog */}
        <Dialog open={showPicturesUpload} onClose={() => setShowPicturesUpload(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Upload Pictures</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Upload pictures by taking photos or selecting from your device.
              </Typography>
              <QuotationUpload
                leadId={parseInt(id!)}
                onUploadComplete={(files) => handlePicturesUpload(files)}
                uploading={uploadingPictures}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowPicturesUpload(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>

        {/* Document Upload Dialog */}
        <Dialog open={showDocumentUpload} onClose={() => setShowDocumentUpload(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Upload Documents</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Upload documents by selecting files from your device.
              </Typography>
              <QuotationUpload
                leadId={parseInt(id!)}
                onUploadComplete={(files) => handleDocumentUpload(files)}
                uploading={uploadingDocument}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDocumentUpload(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>

        {/* Document Viewer - Simple overlay for tablets, Dialog for desktop */}
        {isTablet && selectedDocument && (selectedDocument.mime_type?.startsWith('image/') || !selectedDocument.filename?.toLowerCase().endsWith('.pdf')) && !selectedDocument.isWordDoc ? (
          // Simple lightweight image viewer for tablets
          <Box
            onClick={() => {
              setSelectedDocument(null);
              setZoomLevel(1);
            }}
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedDocument(null);
                setZoomLevel(1);
              }}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                zIndex: 10,
              }}
            >
              <Close />
            </IconButton>
            <img
              src={selectedDocument.viewUrl || selectedDocument.fileUrl || selectedDocument.file_path}
              alt={selectedDocument.filename}
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: '95%',
                maxHeight: '90vh',
                objectFit: 'contain',
                transform: selectedDocument.rotation ? `rotate(${selectedDocument.rotation}deg)` : undefined,
              }}
            />
          </Box>
        ) : (
        <Dialog
          open={!!selectedDocument}
          onClose={() => {
            setSelectedDocument(null);
            setZoomLevel(1);
          }}
          maxWidth={false}
          fullWidth
          TransitionProps={{ timeout: isTablet ? 0 : 225 }}
          PaperProps={{
            sx: { maxHeight: '95vh' }
          }}
        >
          <DialogContent sx={{ position: 'relative', padding: 0, backgroundColor: selectedDocument?.isWordDoc ? '#f5f5f5' : '#000', minHeight: '80vh', overflow: 'auto' }}>
            {selectedDocument && (
              <>
                {/* Close button */}
                <IconButton
                  onClick={() => {
                    setSelectedDocument(null);
                    setZoomLevel(1);
                  }}
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    color: selectedDocument.isWordDoc ? '#333' : 'white',
                    backgroundColor: selectedDocument.isWordDoc ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.5)',
                    zIndex: 10,
                    '&:hover': {
                      backgroundColor: selectedDocument.isWordDoc ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.7)'
                    }
                  }}
                >
                  <Close />
                </IconButton>

                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', width: '100%' }}>
                  {selectedDocument.isWordDoc ? (
                    // Word document - show preview on mobile/tablet, download button on desktop
                    isMobileOrTablet ? (
                      // Mobile/Tablet: Show docx preview
                      <Box sx={{ width: '100%', height: '80vh', overflow: 'auto', backgroundColor: '#fff', p: 0, pt: 5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1, px: 1, pr: 8 }}>
                          <Typography variant="subtitle1" sx={{ color: '#333', fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {selectedDocument.description || selectedDocument.document_name || selectedDocument.filename}
                          </Typography>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<GetApp />}
                            onClick={async () => {
                              try {
                                const url = selectedDocument.viewUrl || `/api/documents/download/${selectedDocument.id}`;
                                const response = await fetch(url);
                                const blob = await response.blob();
                                let filename = selectedDocument.filename || selectedDocument.original_name || 'document';
                                if (!filename.toLowerCase().endsWith('.docx') && !filename.toLowerCase().endsWith('.doc')) {
                                  filename += '.docx';
                                }
                                const blobUrl = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = blobUrl;
                                link.download = filename;
                                link.style.display = 'none';
                                document.body.appendChild(link);
                                link.click();
                                setTimeout(() => {
                                  document.body.removeChild(link);
                                  window.URL.revokeObjectURL(blobUrl);
                                }, 100);
                              } catch (error) {
                                console.error('Download failed:', error);
                                window.open(selectedDocument.viewUrl || `/api/documents/download/${selectedDocument.id}`, '_blank');
                              }
                            }}
                          >
                            Download
                          </Button>
                        </Box>
                        {docxPreviewLoading && (
                          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                            <CircularProgress />
                            <Typography sx={{ ml: 2 }}>Loading preview...</Typography>
                          </Box>
                        )}
                        {docxError && (
                          <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography color="error" sx={{ mb: 2 }}>Error: {docxError}</Typography>
                            <Typography variant="body2" sx={{ color: '#666' }}>Please use the download button above.</Typography>
                          </Box>
                        )}
                        <Box
                          ref={docxContainerRef}
                          sx={{
                            minHeight: '200px',
                            width: '100%',
                            overflowX: 'auto',
                            '& .docx-wrapper': {
                              background: '#fff',
                              padding: '5px',
                              transform: 'scale(0.85)',
                              transformOrigin: 'top center',
                            },
                            '& .docx-wrapper > section.docx': {
                              boxShadow: '0 0 10px rgba(0,0,0,0.1)',
                              marginBottom: '10px',
                            }
                          }}
                        />
                      </Box>
                    ) : (
                      // Desktop: Show download button
                      <Box sx={{ textAlign: 'center', p: 4 }}>
                        <Description sx={{ fontSize: 80, color: '#1976d2', mb: 2 }} />
                        <Typography variant="h6" sx={{ mb: 2, color: '#333' }}>
                          {selectedDocument.description || selectedDocument.document_name || selectedDocument.filename}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
                          This document cannot be previewed. Click below to download.
                        </Typography>
                        <Button
                          variant="contained"
                          size="large"
                          startIcon={<GetApp />}
                          onClick={async () => {
                            try {
                              const url = selectedDocument.viewUrl || `/api/documents/download/${selectedDocument.id}`;
                              const response = await fetch(url);
                              const blob = await response.blob();
                              let filename = selectedDocument.filename || selectedDocument.original_name || 'document';
                              if (!filename.toLowerCase().endsWith('.docx') && !filename.toLowerCase().endsWith('.doc')) {
                                filename += '.docx';
                              }
                              const blobUrl = window.URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = blobUrl;
                              link.download = filename;
                              link.style.display = 'none';
                              document.body.appendChild(link);
                              link.click();
                              setTimeout(() => {
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(blobUrl);
                              }, 100);
                              setSelectedDocument(null);
                            } catch (error) {
                              console.error('Download failed:', error);
                              window.open(selectedDocument.viewUrl || `/api/documents/download/${selectedDocument.id}`, '_blank');
                            }
                          }}
                          sx={{ mt: 2 }}
                        >
                          Download Document
                        </Button>
                      </Box>
                    )
                  ) : selectedDocument.mime_type?.startsWith('image/') || !selectedDocument.filename?.toLowerCase().endsWith('.pdf') ? (
                    <img
                      src={selectedDocument.viewUrl || selectedDocument.fileUrl || selectedDocument.file_path}
                      alt={selectedDocument.filename}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '80vh',
                        transform: `scale(${zoomLevel})${selectedDocument.rotation ? ` rotate(${selectedDocument.rotation}deg)` : ''}`,
                        transition: isTablet ? 'none' : 'transform 0.3s',
                      }}
                    />
                  ) : (
                    <embed
                      src={selectedDocument.viewUrl || selectedDocument.fileUrl || selectedDocument.file_path}
                      type="application/pdf"
                      width="100%"
                      height="600px"
                      style={{
                        minHeight: '80vh',
                      }}
                    />
                  )}
                </Box>

                {/* Zoom and Print controls for images */}
                {(selectedDocument.mime_type?.startsWith('image/') || (!selectedDocument.isWordDoc && !selectedDocument.filename?.toLowerCase().endsWith('.pdf'))) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      right: 16,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      borderRadius: 2,
                      padding: '8px 16px',
                      zIndex: 10,
                    }}
                  >
                    <Tooltip title="Zoom Out">
                      <IconButton
                        onClick={() => setZoomLevel(prev => Math.max(prev - 0.25, 0.25))}
                        disabled={zoomLevel <= 0.25}
                        sx={{ color: 'white' }}
                        size="small"
                      >
                        <ZoomOut />
                      </IconButton>
                    </Tooltip>
                    <Typography variant="body2" sx={{ color: 'white', minWidth: 50, textAlign: 'center' }}>
                      {Math.round(zoomLevel * 100)}%
                    </Typography>
                    <Tooltip title="Zoom In">
                      <IconButton
                        onClick={() => setZoomLevel(prev => Math.min(prev + 0.25, 3))}
                        disabled={zoomLevel >= 3}
                        sx={{ color: 'white' }}
                        size="small"
                      >
                        <ZoomIn />
                      </IconButton>
                    </Tooltip>
                    <Box sx={{ width: 1, borderLeft: '1px solid rgba(255,255,255,0.3)', mx: 1, height: 24 }} />
                    <Tooltip title="Print">
                      <IconButton
                        onClick={() => {
                          const imageUrl = selectedDocument.viewUrl || selectedDocument.fileUrl || selectedDocument.file_path;
                          const printWindow = window.open('', '_blank');
                          if (printWindow) {
                            printWindow.document.write(`
                              <!DOCTYPE html>
                              <html>
                                <head>
                                  <title>Print - ${selectedDocument.filename || 'Image'}</title>
                                  <style>
                                    body {
                                      margin: 0;
                                      padding: 20px;
                                      display: flex;
                                      justify-content: center;
                                      align-items: center;
                                      min-height: 100vh;
                                      box-sizing: border-box;
                                    }
                                    img {
                                      max-width: 100%;
                                      max-height: 100vh;
                                      object-fit: contain;
                                    }
                                    @media print {
                                      body {
                                        padding: 0;
                                      }
                                      img {
                                        max-width: 100%;
                                        max-height: 100%;
                                      }
                                    }
                                  </style>
                                </head>
                                <body>
                                  <img src="${imageUrl}" alt="${selectedDocument.filename || 'Image'}" onload="window.print(); window.close();" />
                                </body>
                              </html>
                            `);
                            printWindow.document.close();
                          }
                        }}
                        sx={{ color: 'white' }}
                        size="small"
                      >
                        <Print />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>
        )}

        {/* Email Document Modal */}
        <EmailDocumentModal
          open={emailModalOpen}
          onClose={() => {
            setEmailModalOpen(false);
            setSelectedDocForEmail(null);
            setSelectedDocsForEmail([]);
          }}
          document={selectedDocsForEmail.length === 0 ? selectedDocForEmail : undefined}
          documents={selectedDocsForEmail.length > 0 ? selectedDocsForEmail : undefined}
          availableItems={[
            ...photos.filter((p: any) => p.photo_type !== 'document').map((p: any) => ({
              ...p,
              source_table: p.source_table || 'project_photos'
            })),
            ...documents.map((d: any) => ({
              ...d,
              source_table: d.source_table || d.source_type || 'documents'
            }))
          ]}
          customerEmail={project?.customer_email || ''}
          customerName={project?.customer_name || ''}
          customerAddress={[
            project?.customer_address_1,
            project?.customer_town,
            project?.customer_postcode
          ].filter(Boolean).join(', ')}
          projectId={id}
          onSuccess={() => {
            setEmailModalOpen(false);
            setSelectedDocForEmail(null);
            setSelectedDocsForEmail([]);
            fetchDocuments();
            fetchPhotos();
          }}
        />

        {/* Issue Order Modal */}
        <Dialog open={issueOrderModalOpen} onClose={() => setIssueOrderModalOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Create Supplier Orders</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom>
                Select which suppliers to generate order documents for:
              </Typography>
              {supplierOrderOptions.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  No suppliers found with order items.
                </Typography>
              ) : (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Button
                      size="small"
                      onClick={() => setSelectedSupplierKeys(supplierOrderOptions.map(s => s.key))}
                    >
                      Select All
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setSelectedSupplierKeys([])}
                    >
                      Deselect All
                    </Button>
                  </Box>
                  {supplierOrderOptions.map((supplier) => (
                    <Box
                      key={supplier.key}
                      sx={{
                        p: 2,
                        mb: 1,
                        border: '1px solid',
                        borderColor: selectedSupplierKeys.includes(supplier.key) ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        backgroundColor: selectedSupplierKeys.includes(supplier.key) ? 'action.selected' : 'background.paper',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                      onClick={() => {
                        setSelectedSupplierKeys(prev =>
                          prev.includes(supplier.key)
                            ? prev.filter(k => k !== supplier.key)
                            : [...prev, supplier.key]
                        );
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Checkbox
                          checked={selectedSupplierKeys.includes(supplier.key)}
                          onChange={(e) => {
                            e.stopPropagation();
                            setSelectedSupplierKeys(prev =>
                              prev.includes(supplier.key)
                                ? prev.filter(k => k !== supplier.key)
                                : [...prev, supplier.key]
                            );
                          }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {supplier.supplierName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {supplier.categories} • {supplier.itemCount} item(s)
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Documents will be created in the Documents section below.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIssueOrderModalOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={async () => {
                setIssueOrderModalOpen(false);
                await handleIssueOrders(selectedSupplierKeys);
              }}
              disabled={selectedSupplierKeys.length === 0}
            >
              Create Selected ({selectedSupplierKeys.length})
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Remedial Confirmation Dialog */}
        <Dialog
          open={deleteRemedialId !== null}
          onClose={() => setDeleteRemedialId(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete Remedial Issue</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this remedial issue? This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteRemedialId(null)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                if (deleteRemedialId) {
                  handleDeleteRemedial(deleteRemedialId);
                  setDeleteRemedialId(null);
                }
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Send to Supplier Confirmation Modal */}
        <Dialog
          open={supplierSendModalOpen}
          onClose={() => {
            setSupplierSendModalOpen(false);
            setSupplierSendDoc(null);
            setSupplierSendDocs([]);
            setSupplierSendInfo(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShipping color="primary" />
            Send to Supplier
          </DialogTitle>
          <DialogContent>
            {loadingSupplierInfo ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : supplierSendInfo ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                  Please confirm you want to send {supplierSendInfo.fileNames.length > 1 ? 'these orders' : 'this order'}:
                </Typography>

                <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Supplier:</strong> {supplierSendInfo.supplierName}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>To:</strong> {supplierSendInfo.supplierEmail}
                  </Typography>
                  {supplierSendInfo.ccEmails.length > 0 && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>CC:</strong> {supplierSendInfo.ccEmails.join(', ')}
                    </Typography>
                  )}
                  {supplierSendInfo.fileNames.length > 1 ? (
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Attachments ({supplierSendInfo.fileNames.length}):</strong>
                      </Typography>
                      {supplierSendInfo.fileNames.map((name: string, idx: number) => (
                        <Typography key={idx} variant="body2" sx={{ pl: 2 }}>
                          • {name}
                        </Typography>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2">
                      <strong>Attachment:</strong> {supplierSendInfo.fileName}
                    </Typography>
                  )}
                </Box>

                {supplierSendInfo.supplierEmail === 'No email found' && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    No email address found for this supplier. Please add an email in the supplier settings.
                  </Alert>
                )}
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setSupplierSendModalOpen(false);
              setSupplierSendDoc(null);
              setSupplierSendDocs([]);
              setSupplierSendInfo(null);
            }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirmSendToSupplier}
              disabled={loadingSupplierInfo || supplierSendInfo?.supplierEmail === 'No email found'}
              startIcon={<Send />}
            >
              Send Email
            </Button>
          </DialogActions>
        </Dialog>

        {/* Supplier Order Sent Success Modal */}
        <Dialog
          open={supplierSentModalOpen}
          onClose={() => setSupplierSentModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogContent sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'success.main' }}>
              Order Sent Successfully!
            </Typography>
            {supplierSentDetails && (
              <Box sx={{ mt: 3, textAlign: 'left', bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Sent to:</strong> {supplierSentDetails.sentTo}
                </Typography>
                {supplierSentDetails.ccTo.length > 0 && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>CC'd:</strong> {supplierSentDetails.ccTo.join(', ')}
                  </Typography>
                )}
                <Typography variant="body2">
                  <strong>Attachment:</strong> {supplierSentDetails.fileName}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button
              variant="contained"
              color="success"
              onClick={() => setSupplierSentModalOpen(false)}
              size="large"
            >
              Done
            </Button>
          </DialogActions>
        </Dialog>

        {/* Postcode Lookup Dialog */}
        <Dialog open={postcodeDialogOpen} onClose={() => setPostcodeDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Postcode Lookup</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 1 }}>
              <TextField
                fullWidth
                label="Enter Postcode"
                value={postcodeSearch}
                onChange={(e) => setPostcodeSearch(e.target.value)}
                placeholder="e.g. BL1 1AA"
                onKeyPress={(e) => e.key === 'Enter' && handlePostcodeLookup()}
              />
              <Button
                variant="contained"
                onClick={handlePostcodeLookup}
                disabled={loadingAddresses || !postcodeSearch.trim()}
              >
                {loadingAddresses ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Search'
                )}
              </Button>
            </Box>

            {addressOptions.length > 0 && (
              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {addressOptions.map((address, index) => (
                  <ListItem
                    key={index}
                    component="button"
                    onClick={() => handleSelectAddress(address)}
                    sx={{
                      '&:hover': { backgroundColor: 'action.hover' },
                      cursor: 'pointer',
                      border: 'none',
                      width: '100%',
                      textAlign: 'left',
                      backgroundColor: 'transparent'
                    }}
                  >
                    <ListItemText
                      primary={address.address1}
                      secondary={`${address.postTown}, ${address.county} ${address.postcode}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPostcodeDialogOpen(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>

        {/* Rename Photo Dialog */}
        <Dialog
          open={renamePhotoDialog.open}
          onClose={() => setRenamePhotoDialog({ open: false, photo: null, newName: '' })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Rename Photo</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              label="Photo Name"
              value={renamePhotoDialog.newName}
              onChange={(e) => setRenamePhotoDialog(prev => ({ ...prev, newName: e.target.value }))}
              sx={{ mt: 1 }}
              onKeyPress={(e) => e.key === 'Enter' && handleRenamePhoto()}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRenamePhotoDialog({ open: false, photo: null, newName: '' })}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleRenamePhoto}
              disabled={!renamePhotoDialog.newName.trim()}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={snackbarSeverity === 'error' ? null : 6000}
          onClose={() => { setSnackbarOpen(false); setSnackbarSeverity('success'); }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert onClose={() => { setSnackbarOpen(false); setSnackbarSeverity('success'); }} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
  } catch (error) {
    console.error('Error rendering ProjectDetail:', error);
    return (
      <Box p={3}>
        <Typography color="error" variant="h6">An error occurred while loading the project</Typography>
        <Typography variant="body2" sx={{ mt: 2 }}>Error details: {error instanceof Error ? error.message : 'Unknown error'}</Typography>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Back
        </Button>
      </Box>
    );
  }
}